/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  ButtonProps,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Tab,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactComponent as Export } from "../../assets/icons/export.svg";
import { COLORS } from "../../assets/theme";
import AccountNamesTable from "../../components/AccountnameDialog";
import BulkRateRouteContent from "../../components/BulkRateRouteComponent";
import BulkMnpHlrContent from "../../components/BulkMnpHlrComponent";
import CostBreakdownWithDialog from "../../components/CostBreakdownWithDialog";
import SafeHtml from "../../components/SafeHtml";
import RateBreakdownWithDialog from "../../components/RateBreakdownWithDialog";
import Actions from "../../components/common/Actions";
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomButton from "../../components/common/CustomButton";
import CustomDialog from "../../components/common/CustomDialog";
import CustomLabel from "../../components/common/CustomLabel";
import SearchForm from "../../components/common/SearchForm";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ImpactedInstructions from "../../components/ImpactedInstructions";
import ToastNotification from "../../components/ToastNotification";
import {
  IClients,
  useClientAccountQuery,
  useClientsQuery,
  useCustomerOptionsQuery,
} from "../../services/ClientService";
import { fetchCostPlan } from "../../services/CostPlanService";
import { fetchCountry } from "../../services/CountryService";
import { fetchCurrency } from "../../services/CurrencyService";
import { fetchOperator } from "../../services/OperatorsService";
import { fetchProfile } from "../../services/ProfilesService";
import {
  fetchProvider,
  useProviderOptionQuery,
} from "../../services/ProviderService";
import { fetchRoutingCosts } from "../../services/RoutingCostsService";
import {
  bulkMnpHlrApi,
  BulkMnpHlrRequest,
  BulkMnpHlrRequestType,
  bulkRateRouteApi,
  BulkRateRouteRequest,
  BulkRateRouteRequestType,
  BulkRateRouteResponse,
  CustomerBulkApi,
  deleteRoutingPlanDetailsApi,
  exportRoutingPlanDetails,
  fetchLiveCost,
  fetchRoutingPlanDetailsHistory,
  getRoutingPlanDetailsById,
  IBulkCustomer,
  LiveCostResponseItem,
  previewRateNotificationApi,
  RateNotificationPreview,
  RateType,
  RoutingPlanDetailsBulkApi,
  RoutingPlanDetailsType,
  sendRateNotificationApi,
  useNoQuery,
  useRoutingPlanDetailsQuery,
  useRoutingPlansWithRateChangeQuery,
} from "../../services/RoutingPlanDetailsService";
import {
  fetchRoutingPlan,
  useRoutingPlanQuery,
  useRoutingPlansQuery,
} from "../../services/RoutingPlanService";
import { useUsersByEmailQuery } from "../../services/UsersService";
import {
  fetchAllServiceProviders,
  getServiceProviderById,
} from "../../services/ServiceProviderService";
import {
  ErrorResponse,
  IAction,
  IField,
  Option,
  Params,
} from "../../types/table/interfaces.type";
import {
  loadCountriesOptions,
  loadOperatorsByCountry,
} from "../../utils/commonDataUtils";
import { GridDeleteIcon } from "@mui/x-data-grid";
import History from "../../components/History";
import AutoComplete, {
  IAutoCompleteProps,
} from "../../components/common/AutoComplete";
import DataTableBody from "../../components/common/table/DataTableBody";
import TruncatedText from "../../components/common/TruncatedText";
import { RateCsvRecord } from "../../services/RoutingPlanDetailsService";
import {
  EmailTemplateType,
  fetchEmailTemplates,
} from "../../services/EmailTemplateService";
import { hasJwtPermission } from "../../services/ServicesHelpers";

interface Filters extends Params<RoutingPlanDetailsType> {
  countryId?: number;
  providerId?: string;
  routingPlanId?: string | string[];
  rateFilter?: string;
}
const DEFAULT_TEST_EMAIL = "rates@mircoms.com";

interface DialogData extends Partial<
  Omit<
    RoutingPlanDetailsType & IBulkCustomer,
    "operatorId" | "customerId" | "routingPlanId"
  > & { countryId: number; routingPlanId?: string | string[] }
> {
  operatorId?: number | number[];
  customerId?: number | number[];
  routingPlanId?: string | string[];
  effectiveDate?: Date;
}

const RoutingPlanDetailsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [countriesOptions, setCountriesOptions] = useState<Option[]>([]);
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
  const [providersOptions, setProvidersOptions] = useState<Option[]>([]);
  const [hlrServiceProviderOptions, setHlrServiceProviderOptions] = useState<
    Option[]
  >([]);
  const [mnpServiceProviderOptions, setMnpServiceProviderOptions] = useState<
    Option[]
  >([]);
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [filters, setFilters] = useState<
    Filters & { clientId?: string; userId?: string; search: string }
  >({
    search: "",
  });
  const [appliedFilters, setAppliedFilters] = useState<Filters>({});
  const [showTable, setShowTable] = useState(false);
  const [userOptions, setUserOptions] = useState<Option[]>([]);
  const { data: clientData = { content: [] } } = useClientsQuery({ size: 10000 });
  const clientOptions: Option[] = clientData.content
    .filter((client: IClients) => Number(client.id) >= 0)
    .map((client: IClients) => ({
      value: client.id,
      label: client.name,
    }));
  const [RoutingPlanDetailsOptions, setRoutingPlanDetailsOptions] = useState<
    Option[]
  >([]);
  const [blendingDialogOpen, setBlendingDialogOpen] = useState(false);
  const [selectedBlendingData, setSelectedBlendingData] =
    useState<RoutingPlanDetailsType | null>(null);

  const { data: routingPlansOptions = [] } = useRoutingPlansQuery();

  const { data: providerOptions = [] } = useProviderOptionQuery();
  const { data: allPlanDetailsData = { content: [] } } = useRoutingPlanQuery(
    {},
  );
  console.log("allPlanDetailsData", allPlanDetailsData);
  const unsentCount = allPlanDetailsData.content.filter(
    (row) => row.rate !== row.lastRate,
  ).length;

  const { data: fetchUsersByEmail = [] } = useUsersByEmailQuery();

  const dialogRef = useRef<RoutingPlanDialogRef>(null);

  const handleOpenDialog = (
    mode:
      | "add"
      | "edit"
      | "delete"
      | "Bulk Customer"
      | "Send Rate"
      | "Accounts"
      | "impacted-instructions"
      | "Bulk Rate / Route"
      | "Bulk MNP / HLR"
      | "history",
    id?: string,
  ) => {
    dialogRef.current?.openDialog(mode, id);
  };
  const { data: accountsData } = useClientAccountQuery({ name: "" });

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const countriesAsOptions = await loadCountriesOptions();
        setCountriesOptions(countriesAsOptions);
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, [accountsData]);

  useEffect(() => {
    if (providerOptions) {
      setProvidersOptions(providerOptions);
    }
    if (routingPlansOptions) {
      setRoutingPlanDetailsOptions(routingPlansOptions);
    }

    if (fetchUsersByEmail) {
      setUserOptions(fetchUsersByEmail);
    }
  }, [providerOptions, routingPlansOptions]);

  useEffect(() => {
    const loadServiceProviders = async () => {
      try {
        const allServiceProviders = await fetchAllServiceProviders();

        const hlrProviders = allServiceProviders
          .filter(
            (sp) =>
              (sp.type === "HLR" || sp.type === "MNP_HLR") &&
              sp.enabled === true &&
              sp.isDeleted === false,
          )
          .map((sp) => ({
            value: sp.id,
            label: sp.name,
          }));

        const mnpProviders = allServiceProviders
          .filter(
            (sp) =>
              (sp.type === "MNP" || sp.type === "MNP_HLR") &&
              sp.enabled === true &&
              sp.isDeleted === false,
          )
          .map((sp) => ({
            value: sp.id,
            label: sp.name,
          }));

        setHlrServiceProviderOptions(hlrProviders);
        setMnpServiceProviderOptions(mnpProviders);
      } catch (error) {
        console.error("Error loading service providers:", error);
      }
    };

    loadServiceProviders();
  }, []);

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev) => ({ ...prev, search, userId: filters.userId }));
  };

  const handleSearchClick = () => {
    setAppliedFilters({ ...filters });
    setShowTable(true);
    setSelectedRowIds([]);
  };

  const actions: IAction<RoutingPlanDetailsType>[] = Actions({
    handleOpenEditModal: {
      handler: async (id: string | number) => {
        handleOpenDialog("edit", String(id));
      },
      isDisabled: !hasJwtPermission("GET_RATED_ROUTING"),
    },
    // onDelete: {
    //   handler: async (id: string | number) => {
    //     handleOpenDialog("delete", String(id));
    //   },
    //   isDisabled: false,
    // },
    onViewHistory: {
      handler: async (id: number | string) => {
        handleOpenDialog("history", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_RATED_ROUTINGS_HISTORY"),
    },
    onPolyline: {
      handler: async (id: string | number) => {
        handleOpenDialog("impacted-instructions", String(id));
      },
      isDisabled: !hasJwtPermission("GET_ALL_INSTRUCTIONS"),
    },
    onListAccounts: {
      handler: (row: any) => {
        console.log(
          `List Accounts action triggered for routingPlanId: ${row.routingPlanId}`,
        );
        handleOpenDialog("Accounts", String(row.routingPlanId));
      },
      isDisabled: !hasJwtPermission("GET_RATED_ROUTING_ACCOUNTS"),
    },
  });

  const handleCountryChange = async (
    value: string | number | (string | number)[],
  ) => {
    const selectedCountry = Array.isArray(value) ? value[0] : value;

    if (selectedCountry) {
      try {
        const countryId = Number(selectedCountry);
        if (isNaN(countryId)) {
          throw new Error("Invalid country ID");
        }
        setIsOperatorDisabled(true);
        const ops = await loadOperatorsByCountry(countryId);
        setOperatorsByCountry(ops);
      } catch (error) {
        console.error("Error fetching operators for selected country:", error);
        setOperatorsByCountry([]);
      } finally {
        setIsOperatorDisabled(false);
      }
    } else {
      setOperatorsByCountry([]);
      setIsOperatorDisabled(false);
    }
  };

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Routing Plan",
      value: filters.routingPlanId ?? "",
      options: RoutingPlanDetailsOptions,
      clearable: true,
      placeholder: "Select Routing Plan",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedPlan = RoutingPlanDetailsOptions.find(
          (option) => String(option.value) === String(value),
        );
        setFilters((prev) => ({
          ...prev,
          routingPlanId: String(selectedPlan?.value ?? ""),
        }));
      },
    },
    {
      label: "Customer",
      value: filters.clientId ?? "",
      options: clientOptions,
      clearable: true,
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          clientId: String(value),
        }));
      },
    },
    {
      label: "Provider",
      value: filters.providerId ?? "",
      options: providersOptions,
      clearable: true,
      placeholder: "Select Provider",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProvider = providersOptions.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          providerId: String(selectedProvider?.value ?? ""),
        }));
      },
    },
    {
      label: "Country",
      value: filters.countryId ?? "",
      options: countriesOptions,
      placeholder: "Select Country",
      clearable: true,
      onChange: async (value: string | number | (string | number)[]) => {
        const selectedCountry = countriesOptions.find(
          (option) => option.value === value,
        );

        setFilters((prev) => ({
          ...prev,
          countryId: selectedCountry?.value
            ? Number(selectedCountry.value)
            : undefined,
          operatorId: undefined,
        }));

        if (selectedCountry) {
          try {
            setIsOperatorDisabled(true);
            const ops = await loadOperatorsByCountry(
              Number(selectedCountry.value),
            );
            setOperatorsByCountry(ops);
          } catch (error) {
            console.error("Error loading operators:", error);
            setOperatorsByCountry([]);
          } finally {
            setIsOperatorDisabled(false);
          }
        } else {
          setOperatorsByCountry([]);
          setIsOperatorDisabled(false);
        }

        try {
          await handleCountryChange(value);
        } catch (error) {
          console.error("Error in handleCountryChange:", error);
        }
      },
    },
    {
      label: "Operator",
      value: filters.operatorId ?? "",
      options: operatorsByCountry,
      disabled: isOperatorDisabled,
      clearable: true,
      placeholder: "Select Operator",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedOperator = operatorsByCountry.find(
          (option) => option.value === value,
        );
        setFilters((prev) => ({
          ...prev,
          operatorId: selectedOperator?.value
            ? Number(selectedOperator.value)
            : undefined,
        }));
      },
    },
    {
      label: "Employee",
      value: filters.userId ?? "",
      options: fetchUsersByEmail.map((option) => ({
        ...option,
        label: option.name,
        value: option.value,
      })),
      clearable: true,
      onChange: (value: string | number | (string | number)[]) => {
        setFilters((prev) => ({
          ...prev,
          userId: String(value),
        }));
      },
    },
    {
      label: "Rate Change",
      value: filters.rateFilter === undefined ? "" : String(filters.rateFilter),
      clearable: true,
      options: [
        { label: "Change", value: "CHANGE" },
        { label: "Rate loss", value: "LOSS" },
        { label: "No Change", value: "NO_CHANGE" },
      ],
      onChange: (value: string | number | (string | number)[]) => {
        if (Array.isArray(value)) return;
        setFilters((prev) => ({
          ...prev,

          rateFilter: typeof value === "string" ? value : undefined,
        }));
      },
    },
  ];

  const isSearchDisabled = !Object.values(filters).some((value) => {
    if (typeof value === "string") {
      return value.trim() !== "";
    }
    return value !== undefined && value !== null;
  });

  const buttons = [
    {
      label: "Search",
      variant: "contained" as ButtonProps["variant"],
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      disabled: isSearchDisabled || !hasJwtPermission("GET_ALL_RATED_ROUTINGS"),
    },
  ];

  const fields = useMemo<IField<RoutingPlanDetailsType>[]>(
    () => [
      {
        key: "mnpServiceProviderId",
        header: "",
        transform: (t) => {
          const hasMnp = !!t.mnpServiceProviderId;
          const hasHlr = !!t.hlrServiceProviderId;
          if (!hasMnp && !hasHlr) return "-";
          return (
            <Box
              display="flex"
              flexDirection="column"
              gap={0.5}
              alignItems="flex-start"
            >
              {hasMnp && (
                <Tooltip title="MNP Service Provider configured" arrow>
                  <Chip
                    label="MNP"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "10px",
                      fontWeight: 700,
                      backgroundColor: "#1565c0",
                      color: "#fff",
                      borderRadius: "4px",
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                </Tooltip>
              )}
              {hasHlr && (
                <Tooltip title="HLR Service Provider configured" arrow>
                  <Chip
                    label="HLR"
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "10px",
                      fontWeight: 700,
                      backgroundColor: "#2e7d32",
                      color: "#fff",
                      borderRadius: "4px",
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        key: "routingPlanId",
        header: "Routing Plan",
        transform: async (t) => {
          const id = String(t.routingPlanId);
          if (!id) return "-";
          try {
            const plan = await queryClient.fetchQuery({
              queryKey: ["routingPlan", id],
              queryFn: () => fetchRoutingPlan(id),
            });
            return plan?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "providerId",
        header: "Provider",
        transform: async (t) => {
          const p1 = Number(t.percentage1) || 0;
          const p2 = Number(t.percentage2) || 0;
          const p3 = Number(t.percentage3) || 0;
          const mainPercentage = 100 - (p1 + p2 + p3);

          const blendingProviders = [
            t.provider1Id,
            t.provider2Id,
            t.provider3Id,
          ].filter(Boolean);
          const hasBlending = blendingProviders.length > 0;

          try {
            const provider = await queryClient.fetchQuery({
              queryKey: ["provider", t.providerId],
              queryFn: () => fetchProvider(t.providerId),
            });
            const name = provider?.name ?? "-";

            if (!hasBlending) {
              return `${name} (${mainPercentage}%)`;
            }

            return (
              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedBlendingData(t);
                  setBlendingDialogOpen(true);
                }}
                sx={{
                  cursor: "pointer",
                  textDecoration: "underline",
                  color: COLORS.GOLD,
                  "&:hover": {
                    color: COLORS.GOLD,
                    opacity: 0.8,
                  },
                }}
              >
                {`${name} (${mainPercentage}%)`}
              </Link>
            );
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Country",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            if (!operator?.countryId) return "-";
            const country = await queryClient.fetchQuery({
              queryKey: ["country", operator.countryId],
              queryFn: () => fetchCountry(operator.countryId),
            });
            return country?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "Operator",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return operator?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "CC",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return operator?.cc ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "MCC",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return operator?.mcc?.join(",") ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "operatorId",
        header: "MNC",
        transform: async (t) => {
          if (!t.operatorId) return "-";
          try {
            const operator = await queryClient.fetchQuery({
              queryKey: ["operator", t.operatorId],
              queryFn: () => fetchOperator(t.operatorId),
            });
            return <TruncatedText text={operator?.mnc?.join(",")} />;
          } catch {
            return "-";
          }
        },
      },
      {
        key: "cost",
        header: "Total Cost",
        transform: async (t) => {
          const routingCost = t.cost ?? 0;
          const hlrCost = t.hlrCost;
          const mnpCost = t.mnpCost;

          const routingPlanId = Array.isArray(t.routingPlanId)
            ? t.routingPlanId[0]
            : t.routingPlanId;

          const getSpCurrency = async (spId: number | string) => {
            try {
              const sp = await queryClient.fetchQuery({
                queryKey: ["serviceProvider", spId],
                queryFn: () => getServiceProviderById(spId),
              });
              const profile = await queryClient.fetchQuery({
                queryKey: ["profile", sp.profileId],
                queryFn: () => fetchProfile(sp.profileId),
              });
              const currency = await queryClient.fetchQuery({
                queryKey: ["currency", profile.currencyId],
                queryFn: () => fetchCurrency(Number(profile.currencyId)),
              });
              return currency?.name;
            } catch {
              return undefined;
            }
          };

          const getRoutingPlanCurrency = async (planId: string) => {
            try {
              const plan = await queryClient.fetchQuery({
                queryKey: ["routingPlan", planId],
                queryFn: () => fetchRoutingPlan(planId),
              });
              const currencyId = plan?.currencyId;
              if (!currencyId) return undefined;
              const currency = await queryClient.fetchQuery({
                queryKey: ["currency", currencyId],
                queryFn: () => fetchCurrency(currencyId),
              });
              return currency?.name;
            } catch {
              return undefined;
            }
          };

          const getProviderCurrency = async (providerId: string) => {
            try {
              const provider = await queryClient.fetchQuery({
                queryKey: ["provider", providerId],
                queryFn: () => fetchProvider(providerId),
              });
              const profile = await queryClient.fetchQuery({
                queryKey: ["profile", provider.profileId],
                queryFn: () => fetchProfile(provider.profileId),
              });
              const currency = await queryClient.fetchQuery({
                queryKey: ["currency", profile.currencyId],
                queryFn: () => fetchCurrency(Number(profile.currencyId)),
              });
              return currency?.name;
            } catch {
              return undefined;
            }
          };

          const [
            routingCostCurrency,
            mnpCostCurrency,
            hlrCostCurrency,
            convertedCostCurrency,
          ] = await Promise.all([
            t.providerId
              ? getProviderCurrency(t.providerId)
              : Promise.resolve(undefined),
            t.mnpServiceProviderId && mnpCost !== undefined && mnpCost > 0
              ? getSpCurrency(Number(t.mnpServiceProviderId))
              : Promise.resolve(undefined),
            t.hlrServiceProviderId && hlrCost !== undefined && hlrCost > 0
              ? getSpCurrency(Number(t.hlrServiceProviderId))
              : Promise.resolve(undefined),
            routingPlanId
              ? getRoutingPlanCurrency(routingPlanId)
              : Promise.resolve(undefined),
          ]);

          return (
            <CostBreakdownWithDialog
              routingCost={routingCost}
              {...(hlrCost !== undefined && hlrCost !== null
                ? { hlrCost }
                : {})}
              {...(mnpCost !== undefined && mnpCost !== null
                ? { mnpCost }
                : {})}
              routingCostCurrency={routingCostCurrency}
              mnpCostCurrency={mnpCostCurrency}
              hlrCostCurrency={hlrCostCurrency}
              convertedCostCurrency={convertedCostCurrency}
              {...(t.convertedCost !== undefined && t.convertedCost !== null
                ? { convertedRoutingCost: t.convertedCost }
                : {})}
              {...(t.convertedMnpCost !== undefined &&
              t.convertedMnpCost !== null
                ? { convertedMnpCost: t.convertedMnpCost }
                : {})}
              {...(t.convertedHlrCost !== undefined &&
              t.convertedHlrCost !== null
                ? { convertedHlrCost: t.convertedHlrCost }
                : {})}
            />
          );
        },
      },
      {
        key: "rate",
        header: "Rate",
        transform: async (t) => (
          <RateBreakdownWithDialog
            rate={t.rate}
            rateType={t.rateType}
            rateModifier={t.rateModifier}
          />
        ),
      },
      { key: "profit", header: "Profit" },
      {
        key: "hlrServiceProviderId",
        header: "HLR Service Provider",
        transform: async (t) => {
          if (!t.hlrServiceProviderId) return "-";
          try {
            const sp = await queryClient.fetchQuery({
              queryKey: ["serviceProvider", Number(t.hlrServiceProviderId)],
              queryFn: () =>
                getServiceProviderById(Number(t.hlrServiceProviderId)),
            });
            return sp?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      {
        key: "mnpServiceProviderId",
        header: "MNP Service Provider",
        transform: async (t) => {
          if (!t.mnpServiceProviderId) return "-";
          try {
            const sp = await queryClient.fetchQuery({
              queryKey: ["serviceProvider", Number(t.mnpServiceProviderId)],
              queryFn: () =>
                getServiceProviderById(Number(t.mnpServiceProviderId)),
            });
            return sp?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
      { key: "effectiveDate", header: "Effective Date" },
      {
        key: "routingPlanId",
        header: "Currency",
        transform: async (t) => {
          const planId = Array.isArray(t.routingPlanId)
            ? t.routingPlanId[0]
            : t.routingPlanId;
          if (!planId) return "-";
          try {
            const plan = await queryClient.fetchQuery({
              queryKey: ["routingPlan", planId],
              queryFn: () => fetchRoutingPlan(planId),
            });
            const currencyId = plan?.currencyId;
            if (!currencyId) return "-";
            const currency = await queryClient.fetchQuery({
              queryKey: ["currency", currencyId],
              queryFn: () => fetchCurrency(currencyId),
            });
            return currency?.name ?? "-";
          } catch {
            return "-";
          }
        },
      },
    ],
    [queryClient],
  );

  function refresh() {
    setAppliedFilters((prev) => ({ ...prev }));
    queryClient.invalidateQueries();
  }

  // async function sendUpdatedRateSheet() {
  //   await sendRateNotificationApi();
  //   refresh();
  // }

  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);

  useEffect(() => {
    setSelectedRowIds([]);
  }, [filters]);

  const onRowSelect = (row: RoutingPlanDetailsType, checked: boolean) => {
    setSelectedRowIds((prevSelected) => {
      const newSelected = checked
        ? [...prevSelected, String(row.id)]
        : prevSelected.filter((id) => id !== String(row.id));
      console.log("Selected row IDs:", newSelected);
      return newSelected;
    });
  };

  const isRowSelected = (row: RoutingPlanDetailsType) =>
    selectedRowIds.includes(String(row.id));
  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <SearchForm autoCompletes={autoCompletes} buttons={buttons} />
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={2}
          mt={2}
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <CustomButton
            size="large"
            variant="outlined"
            label={`Send Updated Rate Sheet${
              unsentCount > 0 ? ` (${unsentCount})` : ""
            }`}
            onClick={() => handleOpenDialog("Send Rate")}
            textColor={COLORS.GOLD}
            borderColor={COLORS.GOLD}
            disabled={
              unsentCount > 0 ||
              !hasJwtPermission("GET_ALL_RATED_ROUTING_WITH_RATE_CHANGE")
            }
          />
          <CustomButton
            size="medium"
            variant="outlined"
            label="Bulk Customer"
            onClick={() => handleOpenDialog("Bulk Customer")}
            textColor={COLORS.GOLD}
            borderColor={COLORS.GOLD}
            disabled={!hasJwtPermission("BULK_CUSTOMERS")}
          />
          <CustomButton
            size="medium"
            variant="contained"
            onClick={() => handleOpenDialog("add")}
            label="Add Route"
            textColor={COLORS.textPrimary}
            bgColor={COLORS.GOLD}
            disabled={!hasJwtPermission("POST_RATED_ROUTING")}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomLabel
          text="Routing Plan Details List"
          variant={isSmallScreen ? "h6" : "h5"}
          fontWeight={500}
          marginTop={1}
          marginBottom={1}
        />
        <SearchHeaderContainer
          search={filters.search ?? ""}
          setSearch={handleSearchChange}
          buttonConfig={[
            {
              size: "small",
              borderColor: COLORS.GOLD,
              label: "Bulk Rate / Route",
              textColor: COLORS.GOLD,
              disabled:
                selectedRowIds.length === 0 ||
                !hasJwtPermission("BULK_RATE_ROUTING"),
              variant: "outlined",
              onClick: () => handleOpenDialog("Bulk Rate / Route"),
            },
            {
              size: "small",
              borderColor: COLORS.GOLD,
              label: "Bulk MNP / HLR",
              textColor: COLORS.GOLD,
              disabled:
                selectedRowIds.length === 0 ||
                !hasJwtPermission("BULK_MNP_HLR"),
              variant: "outlined",
              onClick: () => handleOpenDialog("Bulk MNP / HLR"),
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              variant: "outlined" as ButtonProps["variant"],
              startIcon: <GridDeleteIcon />,
              onClick: () => handleOpenDialog("delete"),
              textColor: COLORS.iconColor,
              disabled: !hasJwtPermission("DELETE_RATED_ROUTING"),
            },
            {
              size: "small",
              bgColor: COLORS.GOLD,
              startIcon: <Export />,
              disabled: !hasJwtPermission("GET_ALL_RATED_ROUTINGS_CSV"),
              onClick: async () => {
                try {
                  await exportRoutingPlanDetails(appliedFilters);
                  ToastNotification.success("Export completed successfully!");
                } catch (error: any) {
                  ToastNotification.error(
                    error.message || "Export failed. Please try again.",
                  );
                }
              },
              showExportProgress: true,
              textColor: COLORS.iconColor,
              label: "Export",
            },
          ]}
        />

        <CustomTable<RoutingPlanDetailsType>
          filters={appliedFilters}
          fields={fields}
          fetchFunc={showTable ? useRoutingPlanDetailsQuery : useNoQuery}
          actions={actions}
          shadeRow={(row) =>
            (row.profit ?? 0) < 0 ||
            row.lastRate === undefined ||
            Number(row.rate) !== Number(row.lastRate)
          }
          shadeBg={(row) => {
            if ((row.profit ?? 0) < 0) {
              return COLORS.rateLossBackground;
            }
            if (row.lastRate === undefined || row.rate !== row.lastRate) {
              return COLORS.rateChangeBackground;
            }
            return undefined;
          }}
          shadeColor={COLORS.shadeBlack}
          checkboxSelect={true}
          onRowSelect={onRowSelect}
          isRowSelected={isRowSelected}
        />
      </Box>
      <RoutingPlanDialog
        ref={dialogRef}
        onRefresh={refresh}
        selectedRowIds={selectedRowIds}
        onClearSelection={() => setSelectedRowIds([])}
        countriesOptions={countriesOptions}
        customersOptions={clientOptions}
        providersOptions={providersOptions}
        hlrServiceProviderOptions={hlrServiceProviderOptions}
        mnpServiceProviderOptions={mnpServiceProviderOptions}
        RoutingPlanDetailsOptions={RoutingPlanDetailsOptions}
        appliedFilters={appliedFilters}
      />
      {!showTable && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="120px"
        >
          <CustomLabel
            text="Please use the search above to display data."
            variant="body1"
            fontWeight={400}
            color={COLORS.placeHolderColor}
            marginBottom={2}
          />
        </Box>
      )}
      <BlendingProvidersDialog
        open={blendingDialogOpen}
        onClose={() => setBlendingDialogOpen(false)}
        data={selectedBlendingData}
      />
    </Box>
  );
};

interface BlendingProvidersDialogProps {
  open: boolean;
  onClose: () => void;
  data: RoutingPlanDetailsType | null;
}

const BlendingProvidersDialog: React.FC<BlendingProvidersDialogProps> = ({
  open,
  onClose,
  data,
}) => {
  const [providerCosts, setProviderCosts] = useState<{
    provider1: { name: string; percentage: number; cost: number | null } | null;
    provider2: { name: string; percentage: number; cost: number | null } | null;
    provider3: { name: string; percentage: number; cost: number | null } | null;
  }>({
    provider1: null,
    provider2: null,
    provider3: null,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !data) {
      return;
    }

    const loadProviderDetails = async () => {
      setLoading(true);
      try {
        const p1 = Number(data.percentage1) || 0;
        const p2 = Number(data.percentage2) || 0;
        const p3 = Number(data.percentage3) || 0;

        const newCosts: any = {
          provider1: null,
          provider2: null,
          provider3: null,
        };

        // Fetch provider1 if exists
        if (data.provider1Id) {
          const provider1 = await fetchProvider(data.provider1Id);
          const provider1CostResponse = await fetchRoutingCosts({
            operatorId: data.operatorId,
            vendorConnectionProfileId: data.provider1Id,
          });
          const provider1Cost =
            provider1CostResponse.content.length > 0
              ? provider1CostResponse.content[0].cost
              : null;
          newCosts.provider1 = {
            name: provider1.name,
            percentage: p1,
            cost: provider1Cost,
          };
        }

        // Fetch provider2 if exists
        if (data.provider2Id) {
          const provider2 = await fetchProvider(data.provider2Id);
          const provider2CostResponse = await fetchRoutingCosts({
            operatorId: data.operatorId,
            vendorConnectionProfileId: data.provider2Id,
          });
          const provider2Cost =
            provider2CostResponse.content.length > 0
              ? provider2CostResponse.content[0].cost
              : null;
          newCosts.provider2 = {
            name: provider2.name,
            percentage: p2,
            cost: provider2Cost,
          };
        }

        // Fetch provider3 if exists
        if (data.provider3Id) {
          const provider3 = await fetchProvider(data.provider3Id);
          const provider3CostResponse = await fetchRoutingCosts({
            operatorId: data.operatorId,
            vendorConnectionProfileId: data.provider3Id,
          });
          const provider3Cost =
            provider3CostResponse.content.length > 0
              ? provider3CostResponse.content[0].cost
              : null;
          newCosts.provider3 = {
            name: provider3.name,
            percentage: p3,
            cost: provider3Cost,
          };
        }

        setProviderCosts(newCosts);
      } catch (error) {
        console.error("Error loading provider details:", error);
        ToastNotification.error("Failed to load provider details");
      } finally {
        setLoading(false);
      }
    };

    loadProviderDetails();
  }, [open, data]);

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      mode="Bulk Customer"
      title="Blending Provider Details"
      fields={[
        {
          type: "custom",
          component: loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="200px"
            >
              <CircularProgress sx={{ color: COLORS.GOLD }} />
            </Box>
          ) : (
            <TableContainer
              sx={{
                border: `1px solid ${COLORS.dividerColor}`,
                borderRadius: "8px",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Percentage</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cost</TableCell>
                  </TableRow>
                </TableHead>
                <tbody>
                  {providerCosts.provider1 && (
                    <TableRow>
                      <TableCell>
                        Provider 1: {providerCosts.provider1.name}
                      </TableCell>
                      <TableCell>
                        {providerCosts.provider1.percentage}%
                      </TableCell>
                      <TableCell>
                        {providerCosts.provider1.cost !== null
                          ? Math.round(
                              (providerCosts.provider1.cost + Number.EPSILON) *
                                1e7,
                            ) / 1e7
                          : "-"}
                      </TableCell>
                    </TableRow>
                  )}
                  {providerCosts.provider2 && (
                    <TableRow>
                      <TableCell>
                        Provider 2: {providerCosts.provider2.name}
                      </TableCell>
                      <TableCell>
                        {providerCosts.provider2.percentage}%
                      </TableCell>
                      <TableCell>
                        {providerCosts.provider2.cost !== null
                          ? Math.round(
                              (providerCosts.provider2.cost + Number.EPSILON) *
                                1e7,
                            ) / 1e7
                          : "-"}
                      </TableCell>
                    </TableRow>
                  )}
                  {providerCosts.provider3 && (
                    <TableRow>
                      <TableCell>
                        Provider 3: {providerCosts.provider3.name}
                      </TableCell>
                      <TableCell>
                        {providerCosts.provider3.percentage}%
                      </TableCell>
                      <TableCell>
                        {providerCosts.provider3.cost !== null
                          ? Math.round(
                              (providerCosts.provider3.cost + Number.EPSILON) *
                                1e7,
                            ) / 1e7
                          : "-"}
                      </TableCell>
                    </TableRow>
                  )}
                </tbody>
              </Table>
            </TableContainer>
          ),
        },
      ]}
      buttons={[]}
    />
  );
};

export interface RoutingPlanDialogRef {
  openDialog: (
    mode:
      | "add"
      | "edit"
      | "delete"
      | "Bulk Customer"
      | "Send Rate"
      | "Accounts"
      | "impacted-instructions"
      | "Bulk Rate / Route"
      | "Bulk MNP / HLR"
      | "history",
    id?: string,
  ) => void;
}
interface RoutingPlanDialogProps {
  selectedRowIds: string[];
  onRefresh: () => void;
  onClearSelection: () => void;
  customersOptions: Option[];
  countriesOptions: Option[];
  RoutingPlanDetailsOptions: Option[];
  providersOptions: Option[];
  hlrServiceProviderOptions: Option[];
  mnpServiceProviderOptions: Option[];
  appliedFilters: Filters;
}

const RoutingPlanDialog = forwardRef<
  RoutingPlanDialogRef,
  RoutingPlanDialogProps
>(
  (
    {
      onRefresh,
      customersOptions,
      countriesOptions,
      RoutingPlanDetailsOptions,
      providersOptions,
      hlrServiceProviderOptions,
      mnpServiceProviderOptions,
      appliedFilters,
      selectedRowIds,
      onClearSelection,
    },
    ref,
  ) => {
    const queryClient = useQueryClient();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<
      | "add"
      | "edit"
      | "delete"
      | "Bulk Customer"
      | "Send Rate"
      | "Accounts"
      | "impacted-instructions"
      | "Bulk Rate / Route"
      | "Bulk MNP / HLR"
      | "history"
    >("add");

    const [bulkRateRouteRequest, setBulkRateRouteRequest] = useState<
      Partial<BulkRateRouteRequest>
    >({
      type: BulkRateRouteRequestType.RATE,
    });

    const [bulkMnpHlrRequest, setBulkMnpHlrRequest] = useState<
      Partial<BulkMnpHlrRequest>
    >({
      type: BulkMnpHlrRequestType.MNP,
    });

    const [dialogData, setDialogData] = useState<
      DialogData & {
        routingPlanId?: string | string[];
        testEmail?: string;
        sendonlyChange?: boolean;
        updateLastRate?: boolean;
        sendToCustomer?: boolean;
        attachCcEmail?: boolean;
      }
    >({
      effectiveDate: new Date(),
      sendonlyChange: false,
      updateLastRate: true,
      sendToCustomer: true,
      attachCcEmail: true,
    });
    const [selectedId, setSelectedId] = useState<string | undefined>();

    const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
    const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
    const [sendDialogOpen, setSendDialogOpen] = useState(false);
    const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
    const [operatorsWithCostIds, setOperatorsWithCostIds] = useState<
      Set<number>
    >(new Set());
    const [hasFetchedCostPlan, setHasFetchedCostPlan] = useState(false);
    const [
      RoutingPlanWithRateChangeOptions,
      setRoutingPlanWithRateChangeOptions,
    ] = useState<Option[]>([]);
    const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
    const [openNoteDialog, setOpenNoteDialog] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [previewData, setPreviewData] = useState<RateNotificationPreview[]>(
      [],
    );
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewTabIndex, setPreviewTabIndex] = useState(0);
    const [emailTemplatesDialogOpen, setEmailTemplatesDialogOpen] =
      useState(false);
    const [emailTemplates, setEmailTemplates] = useState<{
      rateChange?: EmailTemplateType;
    }>({});
    const [emailTemplatesLoading, setEmailTemplatesLoading] = useState(false);
    const { data: routingPlansWithRateChangeOption } =
      useRoutingPlansWithRateChangeQuery({ enabled: sendDialogOpen });
    useEffect(() => {
      if (routingPlansWithRateChangeOption) {
        setRoutingPlanWithRateChangeOptions(
          routingPlansWithRateChangeOption.map((plan) => ({
            value: plan.value,
            label: plan.label,
          })),
        );
      }
    }, [routingPlansWithRateChangeOption]);
    useEffect(() => {
      if (dialogMode === "Bulk Rate / Route") {
        let isSaveDisabledValue = false;
        if (!selectedRowIds || selectedRowIds.length === 0) {
          isSaveDisabledValue = true;
        } else if (
          bulkRateRouteRequest.type === BulkRateRouteRequestType.RATE
        ) {
          isSaveDisabledValue =
            (bulkRateRouteRequest.rateModifier === undefined ||
              bulkRateRouteRequest.rateModifier === null ||
              String(bulkRateRouteRequest.rateModifier) === "" ||
              !bulkRateRouteRequest.rateType) &&
            !bulkRateRouteRequest.providerId;
        } else if (
          bulkRateRouteRequest.type === BulkRateRouteRequestType.ROUTE
        ) {
          isSaveDisabledValue =
            !bulkRateRouteRequest.providerId &&
            (bulkRateRouteRequest.rateModifier === undefined ||
              bulkRateRouteRequest.rateModifier === null ||
              String(bulkRateRouteRequest.rateModifier) === "" ||
              !bulkRateRouteRequest.rateType);
        } else if (
          bulkRateRouteRequest.type === BulkRateRouteRequestType.RATE_ROUTE
        ) {
          isSaveDisabledValue =
            bulkRateRouteRequest.rateModifier === undefined ||
            bulkRateRouteRequest.rateModifier === null ||
            String(bulkRateRouteRequest.rateModifier) === "" ||
            !bulkRateRouteRequest.rateType ||
            !bulkRateRouteRequest.providerId;
        } else {
          isSaveDisabledValue = true;
        }
        setIsSaveDisabled(isSaveDisabledValue);
      } else if (dialogMode === "Bulk MNP / HLR") {
        let isSaveDisabledValue = false;
        if (!selectedRowIds || selectedRowIds.length === 0) {
          isSaveDisabledValue = true;
        } else {
          isSaveDisabledValue = !bulkMnpHlrRequest.type;
        }
        setIsSaveDisabled(isSaveDisabledValue);
      } else if (dialogMode === "Bulk Customer") {
        const isRoutingPlanEmpty = !dialogData.routingPlanId;
        const isCustomerEmpty =
          !dialogData.customerId ||
          (Array.isArray(dialogData.customerId) &&
            dialogData.customerId.length === 0);
        setIsSaveDisabled(isRoutingPlanEmpty || isCustomerEmpty);
      } else if (dialogMode === "add" || dialogMode === "edit") {
        const isRoutingPlanEmpty = !dialogData.routingPlanId;
        const isOperatorEmpty =
          !dialogData.operatorId ||
          (Array.isArray(dialogData.operatorId) &&
            dialogData.operatorId.length === 0);
        setIsSaveDisabled(isRoutingPlanEmpty || isOperatorEmpty);
      } else {
        const isAnyFieldEmpty = !dialogData.routingPlanId;
        setIsSaveDisabled(isAnyFieldEmpty);
      }
    }, [
      dialogData,
      dialogMode,
      selectedRowIds,
      bulkRateRouteRequest,
      bulkMnpHlrRequest,
    ]);

    const validateField = (field: string, value: string) => {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: !value,
      }));
    };
    const { data: accountNames } = useCustomerOptionsQuery(
      dialogData.id ? dialogData.id : "",
      {
        enabled: dialogOpen && dialogMode === "Accounts",
      },
    );

    useImperativeHandle(ref, () => ({
      openDialog: (mode, id) => {
        if (mode !== "Send Rate") {
          setDialogMode(mode);
          setDialogOpen(true);
        }
        setSelectedId(id);
        openDialog(mode, id);
        if (mode === "Send Rate") {
          setSendDialogOpen(true);
        }
        if (mode === "Bulk MNP / HLR") {
          setBulkMnpHlrRequest({ type: BulkMnpHlrRequestType.MNP });
        }
      },
      openSendDialog: () => {
        setDialogData((prev) => ({
          ...prev,
          routingPlanId: "",
          sendonlyChange: false,
          updateLastRate: true,
          sendToCustomer: true,
          attachCcEmail: true,
          testEmail: undefined,
        }));
        setSendDialogOpen(true);
      },
    }));

    const handleCountryChange = async (
      value: string | number | (string | number)[],
    ) => {
      const selectedCountry = Array.isArray(value) ? value[0] : value;

      setDialogData((prev) => ({
        ...prev,
        countryId: Number(selectedCountry),
        operatorId: undefined,
      }));

      setOperatorsWithCostIds(new Set());
      setHasFetchedCostPlan(false);

      validateField("country", String(selectedCountry));

      if (selectedCountry) {
        try {
          const countryId = Number(selectedCountry);
          if (isNaN(countryId)) {
            throw new Error("Invalid country ID");
          }
          setIsOperatorDisabled(true);
          const ops = await loadOperatorsByCountry(countryId);
          setOperatorsByCountry(ops);
        } catch (error) {
          console.error(
            "Error fetching operators for selected country:",
            error,
          );
          setOperatorsByCountry([]);
        } finally {
          setIsOperatorDisabled(false);
        }
      } else {
        setOperatorsByCountry([]);
        setIsOperatorDisabled(false);
      }
    };

    const fetchAndSetCostPlan = async (
      providerId: string,
      countryId: number,
    ) => {
      try {
        const params: Parameters<typeof fetchCostPlan>[0] & {
          countryId?: number;
        } = {
          vendorConnectionProfileId: providerId,
          countryId,
          size: 9999,
        };
        const result = await fetchCostPlan(
          params as Parameters<typeof fetchCostPlan>[0],
        );
        const ids = new Set(result.content.map((c) => Number(c.operatorId)));
        setOperatorsWithCostIds(ids);
        setHasFetchedCostPlan(true);
      } catch (error) {
        console.error(
          "Error fetching cost plan for operator filtering:",
          error,
        );
      }
    };

    const handleOperatorDropdownOpen = async () => {
      if (!dialogData.providerId || !dialogData.countryId) return;
      await fetchAndSetCostPlan(dialogData.providerId, dialogData.countryId);
    };

    const handleProviderChange = async (
      value: string | number | (string | number)[],
    ) => {
      const newProviderId = String(value);
      setDialogData((prev) => ({
        ...prev,
        providerId: newProviderId,
        operatorId: undefined,
      }));
      setOperatorsWithCostIds(new Set());
      setHasFetchedCostPlan(false);
      validateField("providerId", newProviderId);
      if (newProviderId && dialogData.countryId) {
        await fetchAndSetCostPlan(newProviderId, dialogData.countryId);
      }
    };

    const sortedOperatorsByCountry = useMemo(() => {
      if (!hasFetchedCostPlan || operatorsWithCostIds.size === 0) {
        return operatorsByCountry;
      }
      const withCost = operatorsByCountry.filter((op) =>
        operatorsWithCostIds.has(Number(op.value)),
      );
      const withoutCost = operatorsByCountry.filter(
        (op) => !operatorsWithCostIds.has(Number(op.value)),
      );
      return [...withCost, ...withoutCost];
    }, [operatorsByCountry, operatorsWithCostIds, hasFetchedCostPlan]);

    const isOperatorEffectivelyDisabled =
      isOperatorDisabled || !dialogData.providerId || !dialogData.countryId;

    const rateTypeOptions = [
      { label: "Exact Value", value: RateType.VALUE },
      { label: "Multiple of Cost", value: RateType.MULTIPLE_OF_COST },
      { label: "Add to Cost", value: RateType.ADD_TO_COST },
    ];

    const [liveCostByOperator, setLiveCostByOperator] = useState<
      Record<number, LiveCostResponseItem>
    >({});
    const [isLiveCostLoading, setIsLiveCostLoading] = useState(false);
    const liveCostByOperatorRef = useRef<Record<number, LiveCostResponseItem>>(
      {},
    );
    const liveCostAbortRef = useRef<AbortController | null>(null);
    const liveCostDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );

    const computeRate = (
      rateType: RateType | undefined,
      rateModifier: number | undefined,
      cost: number | undefined,
    ): number | undefined => {
      if (rateModifier === undefined || rateModifier === null || isNaN(rateModifier)) {
        return undefined;
      }
      const costValue = cost ?? 0;
      const roundTo6Decimals = (value: number): number =>
        Math.round(value * 1e6) / 1e6;
      switch (rateType) {
        case RateType.MULTIPLE_OF_COST:
          return roundTo6Decimals(rateModifier * costValue);
        case RateType.ADD_TO_COST:
          return roundTo6Decimals(rateModifier + costValue);
        case RateType.VALUE:
        default:
          return roundTo6Decimals(rateModifier);
      }
    };

    const getSelectedOperatorIds = (
      operatorId: DialogData["operatorId"],
    ): number[] => {
      const rawIds = Array.isArray(operatorId) ? operatorId : [operatorId];
      const numericIds = rawIds
        .map(Number)
        .filter((id) => !Number.isNaN(id) && id !== 0);
      return Array.from(new Set(numericIds));
    };

    const getCostForOperator = (operatorId: number | undefined) => {
      if (operatorId === undefined) {
        return undefined;
      }
      return liveCostByOperatorRef.current[operatorId]?.totalConvertedCost;
    };

    const primaryOperatorId = getSelectedOperatorIds(
      dialogData.operatorId,
    )[0];

    const handleRateTypeChange = (value: string | number | (string | number)[]) => {
      const newRateType = (Array.isArray(value) ? value[0] : value) as RateType;
      setDialogData((prev) => ({
        ...prev,
        rateType: newRateType,
        rate: computeRate(
          newRateType,
          prev.rateModifier,
          getCostForOperator(primaryOperatorId),
        ),
      }));
    };

    const handleRateModifierChange = (value: string | number) => {
      const newRateModifier = value === "" ? undefined : Number(value);
      setDialogData((prev) => ({
        ...prev,
        rateModifier: newRateModifier,
        rate: computeRate(
          prev.rateType,
          newRateModifier,
          getCostForOperator(primaryOperatorId),
        ),
      }));
    };

    const getFilteredOperatorId = (): DialogData["operatorId"] => {
      if (!hasFetchedCostPlan || !Array.isArray(dialogData.operatorId)) {
        return dialogData.operatorId;
      }
      const filtered = dialogData.operatorId.filter((id) =>
        operatorsWithCostIds.has(Number(id)),
      );
      return filtered.length > 0 ? filtered : dialogData.operatorId;
    };

    const toLiveCostNumber = (
      value: string | number | undefined | null,
    ): number | undefined => {
      if (value === undefined || value === null || value === "") {
        return undefined;
      }
      const numericValue = Number(value);
      return Number.isNaN(numericValue) ? undefined : numericValue;
    };

    const liveCostOperatorIds = getSelectedOperatorIds(dialogData.operatorId);
    const liveCostOperatorIdsKey = liveCostOperatorIds.join(",");
    const liveCostRoutingPlanId = Array.isArray(dialogData.routingPlanId)
      ? dialogData.routingPlanId[0]
      : dialogData.routingPlanId;

    useEffect(() => {
      if (
        !dialogOpen ||
        (dialogMode !== "add" && dialogMode !== "edit") ||
        liveCostOperatorIds.length === 0 ||
        !dialogData.providerId
      ) {
        return;
      }

      if (liveCostDebounceRef.current) {
        clearTimeout(liveCostDebounceRef.current);
      }

      liveCostDebounceRef.current = setTimeout(() => {
        if (liveCostAbortRef.current) {
          liveCostAbortRef.current.abort();
        }
        const controller = new AbortController();
        liveCostAbortRef.current = controller;
        setIsLiveCostLoading(true);

        fetchLiveCost(
          {
            operatorIds: liveCostOperatorIds,
            routingPlanId: liveCostRoutingPlanId
              ? String(liveCostRoutingPlanId)
              : undefined,
            providerId: toLiveCostNumber(dialogData.providerId),
            provider1Id: toLiveCostNumber(dialogData.provider1Id),
            percentage1: dialogData.percentage1,
            provider2Id: toLiveCostNumber(dialogData.provider2Id),
            percentage2: dialogData.percentage2,
            provider3Id: toLiveCostNumber(dialogData.provider3Id),
            percentage3: dialogData.percentage3,
            hlrServiceProviderId: toLiveCostNumber(
              dialogData.hlrServiceProviderId,
            ),
            mnpServiceProviderId: toLiveCostNumber(
              dialogData.mnpServiceProviderId,
            ),
          },
          controller.signal,
        )
          .then((results) => {
            const costMap: Record<number, LiveCostResponseItem> = {};
            results.forEach((item) => {
              costMap[item.operatorId] = item;
            });
            liveCostByOperatorRef.current = costMap;
            setLiveCostByOperator(costMap);
            setDialogData((prev) => {
              const firstOperatorId = getSelectedOperatorIds(
                prev.operatorId,
              )[0];
              return {
                ...prev,
                rate: computeRate(
                  prev.rateType,
                  prev.rateModifier,
                  firstOperatorId !== undefined
                    ? costMap[firstOperatorId]?.totalConvertedCost
                    : undefined,
                ),
              };
            });
          })
          .catch((error) => {
            if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
              console.error("Error fetching live cost:", error);
            }
          })
          .finally(() => {
            setIsLiveCostLoading(false);
          });
      }, 400);

      return () => {
        if (liveCostDebounceRef.current) {
          clearTimeout(liveCostDebounceRef.current);
        }
      };
    }, [
      dialogOpen,
      dialogMode,
      liveCostOperatorIdsKey,
      liveCostRoutingPlanId,
      dialogData.providerId,
      dialogData.provider1Id,
      dialogData.percentage1,
      dialogData.provider2Id,
      dialogData.percentage2,
      dialogData.provider3Id,
      dialogData.percentage3,
      dialogData.hlrServiceProviderId,
      dialogData.mnpServiceProviderId,
    ]);

    const openDialog = async (
      mode:
        | "add"
        | "edit"
        | "delete"
        | "Bulk Customer"
        | "Send Rate"
        | "Accounts"
        | "impacted-instructions"
        | "Bulk Rate / Route"
        | "Bulk MNP / HLR"
        | "history",
      id?: string,
    ) => {
      setDialogMode(mode);

      try {
        if (mode === "edit") {
          if (!id) {
            console.warn("ID is required to edit a routing plan Details.");
            setIsOperatorDisabled(false);
            return;
          }

          const routingPlanDetailsData = await getRoutingPlanDetailsById(id);
          setDialogData({
            ...routingPlanDetailsData,
            effectiveDate: new Date(),
          });

          if (routingPlanDetailsData.operatorId) {
            const { countryId: selectedCountry } = await fetchOperator(
              routingPlanDetailsData.operatorId,
            );

            if (selectedCountry) {
              const countryId = Number(selectedCountry);
              setIsOperatorDisabled(true);
              const ops = await loadOperatorsByCountry(countryId);
              setOperatorsByCountry(ops);
              setDialogData((prev) => ({
                ...prev,
                countryId: countryId,
                operatorId: Array.isArray(routingPlanDetailsData.operatorId)
                  ? routingPlanDetailsData.operatorId
                  : [routingPlanDetailsData.operatorId],
              }));
              setIsOperatorDisabled(false);
            }
          }
        } else if (mode === "delete" && id) {
          setDialogData({ id });
        } else if (mode === "Bulk Customer") {
          setDialogData({
            routingPlanId: "",
            createdDate: new Date(),
            createdBy: "",
          });
        } else if (mode === "Send Rate") {
          setDialogData({
            routingPlanId: "",
            sendonlyChange: false,
            updateLastRate: true,
            sendToCustomer: true,
            attachCcEmail: true,
            effectiveDate: new Date(),
          });
        } else if (mode === "Bulk Rate / Route") {
          setDialogData({
            routingPlanId: "",
          });
        } else if (mode === "Accounts" && id) {
          setDialogData({ id });
        } else {
          setDialogData({
            routingPlanId: "",
            createdDate: new Date(),
            createdBy: "",
          });
        }
      } catch (error) {
        console.error(
          `Error fetching routing plan details data for ${mode} mode:`,
          error,
        );
      }
    };

    const closeDialog = () => {
      setDialogOpen(false);
      setSendDialogOpen(false);
      setIsOperatorDisabled(true);
      setDialogData({ effectiveDate: new Date() });
      if (liveCostDebounceRef.current) {
        clearTimeout(liveCostDebounceRef.current);
      }
      if (liveCostAbortRef.current) {
        liveCostAbortRef.current.abort();
      }
      liveCostByOperatorRef.current = {};
      setLiveCostByOperator({});
      setIsLiveCostLoading(false);
      setBulkRateRouteRequest({
        type: BulkRateRouteRequestType.RATE,
        rateType: RateType.VALUE,
        rateModifier: 0,
        providerId: "",
      });
      setBulkMnpHlrRequest({ type: BulkMnpHlrRequestType.MNP });
    };

    const getChangeColor = (change: string): string => {
      switch (change.toLowerCase()) {
        case "decrease":
          return COLORS.successGreen;
        case "increase":
          return COLORS.errorRed;
        case "new coverage":
          return COLORS.infoBlue;
        case "loss coverage":
          return COLORS.neutralGray;
        default:
          return COLORS.defaultGray;
      }
    };

    const previewFields: IField<RateCsvRecord>[] = [
      { key: "country", header: "Country" },
      { key: "operator", header: "Operator" },
      { key: "mcc", header: "MCC" },
      {
        key: "mnc",
        header: "MNC",
        transform: (row) => <TruncatedText text={row.mnc} />,
      },
      { key: "cc", header: "CC" },
      {
        key: "oldPrice",
        header: "Old Price",
        transform: (row) =>
          row.oldPrice !== null ? row.oldPrice.toFixed(6) : "-",
      },
      {
        key: "newPrice",
        header: "New Price",
        transform: (row) =>
          row.newPrice !== null ? row.newPrice.toFixed(6) : "-",
      },
      { key: "currency", header: "Currency" },
      {
        key: "change",
        header: "Change",
        transform: (row) => (
          <Box
            component="span"
            sx={{
              color: getChangeColor(row.change),
              fontWeight: 600,
              textTransform: "capitalize",
            }}
          >
            {row.change}
          </Box>
        ),
      },
    ];

    const getSelectedRoutingPlanIds = (): string[] => {
      const routingPlanIds = Array.isArray(dialogData.routingPlanId)
        ? dialogData.routingPlanId
        : [String(dialogData.routingPlanId)];
      return routingPlanIds.filter((id) => Boolean(id));
    };

    const fetchRateNotificationPreview = async (
      sendonlyChange = dialogData.sendonlyChange ?? false,
    ): Promise<RateNotificationPreview[] | undefined> => {
      const routingPlanIds = getSelectedRoutingPlanIds();

      if (routingPlanIds.length === 0) {
        ToastNotification.warning("Please select at least one routing plan.");
        return undefined;
      }

      setPreviewLoading(true);
      try {
        const data = await previewRateNotificationApi(
          routingPlanIds,
          sendonlyChange,
        );
        setPreviewData(data);
        return data;
      } catch (error) {
        const typedError = error as ErrorResponse;
        const errorMessage =
          typedError.response?.data?.message ||
          typedError.message ||
          "An error occurred while fetching preview.";
        ToastNotification.error(errorMessage, { timeOut: 3000 });
        return undefined;
      } finally {
        setPreviewLoading(false);
      }
    };

    const handlePreviewClick = async () => {
      setPreviewTabIndex(0);
      const data = await fetchRateNotificationPreview();
      if (data !== undefined) {
        setPreviewDialogOpen(true);
      }
    };

    const handleSendonlyChangeToggle = async (sendonlyChange: boolean) => {
      setDialogData((prev) => ({ ...prev, sendonlyChange }));
      if (previewDialogOpen) {
        setPreviewTabIndex(0);
        await fetchRateNotificationPreview(sendonlyChange);
      }
    };

    const handleViewEmailTemplates = async () => {
      setEmailTemplatesLoading(true);
      try {
        const rateChangeResponse = await fetchEmailTemplates({
          eventType: "RATE_CHANGE",
        } as any);

        setEmailTemplates({
          rateChange: rateChangeResponse.content[0],
        });
        setEmailTemplatesDialogOpen(true);
      } catch (error) {
        const typedError = error as ErrorResponse;
        const errorMessage =
          typedError.response?.data?.message ||
          typedError.message ||
          "An error occurred while fetching email template.";
        ToastNotification.error(errorMessage, { timeOut: 3000 });
      } finally {
        setEmailTemplatesLoading(false);
      }
    };

    const generateUniqueName = (baseName: string, index?: number): string => {
      if (index !== undefined) {
        return `${baseName.trim()}_${index + 1}`;
      }
      const timestamp = Date.now();
      const randomValue = Math.floor(Math.random() * 1000);
      return `${baseName.trim()}_${timestamp}_${randomValue}`;
    };

    const addRoutingPlanDetails = async (data: Partial<DialogData>) => {
      try {
        const {
          operatorId,
          provider1Id,
          provider2Id,
          provider3Id,
          percentage1,
          percentage2,
          percentage3,
          ...restData
        } = data;

        const blendingData: Partial<RoutingPlanDetailsType> = {};
        if (provider1Id && provider1Id !== "") {
          blendingData.provider1Id = provider1Id;
          blendingData.percentage1 = percentage1;
        }
        if (provider2Id && provider2Id !== "") {
          blendingData.provider2Id = provider2Id;
          blendingData.percentage2 = percentage2;
        }
        if (provider3Id && provider3Id !== "") {
          blendingData.provider3Id = provider3Id;
          blendingData.percentage3 = percentage3;
        }

        const commonData: Partial<RoutingPlanDetailsType> = {
          ...restData,
          ...blendingData,
          type: "RATED_ROUTING",
          isDeleted: false,
        };

        console.log("Before sending data:", commonData);

        if (!commonData.name || commonData.name.trim() === "") {
          ToastNotification.error("Name is required for Routing Plan Details.");
          return;
        }

        if (Array.isArray(operatorId) && operatorId.length >= 1) {
          const payloads: Partial<RoutingPlanDetailsType>[] = operatorId.map(
            (id, index) => ({
              ...commonData,
              operatorId: id,
              rate: computeRate(
                commonData.rateType,
                commonData.rateModifier,
                getCostForOperator(Number(id)),
              ),
              name: generateUniqueName(String(commonData.name), index),
            }),
          );
          await RoutingPlanDetailsBulkApi(payloads);
        }

        onRefresh();
        closeDialog();
        ToastNotification.success("Routing plan details added successfully!");
      } catch (error) {
        console.error("Error adding routing plan details:", error);
        const typedError = error as ErrorResponse;
        const errorMessage =
          typedError.response?.data?.message ??
          typedError.message ??
          "An unknown error occurred while adding the routing plan.";
        ToastNotification.error(errorMessage, { timeOut: 3000 });
      }
    };

    const editRoutingPlanDetails = async (data: Partial<DialogData>) => {
      try {
        if (
          !data.id &&
          (!Array.isArray(data.operatorId) || data.operatorId.length === 0)
        ) {
          ToastNotification.warning(
            "Routing Plan Details ID or operatorId is required.",
          );
          return;
        }

        const {
          operatorId,
          provider1Id,
          provider2Id,
          provider3Id,
          percentage1,
          percentage2,
          percentage3,
          ...restData
        } = data;

        const blendingData: Partial<RoutingPlanDetailsType> = {};
        if (provider1Id && provider1Id !== "") {
          blendingData.provider1Id = provider1Id;
          blendingData.percentage1 = percentage1;
        }
        if (provider2Id && provider2Id !== "") {
          blendingData.provider2Id = provider2Id;
          blendingData.percentage2 = percentage2;
        }
        if (provider3Id && provider3Id !== "") {
          blendingData.provider3Id = provider3Id;
          blendingData.percentage3 = percentage3;
        }

        const commonData: Partial<RoutingPlanDetailsType> = {
          ...restData,
          ...blendingData,
          type: "RATED_ROUTING",
          isDeleted: false,
        };

        const operatorIds = Array.isArray(operatorId)
          ? operatorId
          : [operatorId];
        const isMulti = operatorIds.length > 1;
        const payloads: Partial<RoutingPlanDetailsType>[] = operatorIds.map(
          (id, index) => ({
            ...commonData,
            operatorId: id,
            rate: computeRate(
              commonData.rateType,
              commonData.rateModifier,
              getCostForOperator(Number(id)),
            ),
            name:
              isMulti && data.name
                ? generateUniqueName(data.name, index)
                : data.name,
          }),
        );
        await RoutingPlanDetailsBulkApi(payloads);

        onRefresh();
        setDialogOpen(false);
        ToastNotification.success("Routing plan details updated successfully!");
      } catch (error) {
        console.error("Error updating routing plan details:", error);
        const typedError = error as ErrorResponse;
        const errorMessage =
          typedError.response?.data?.message ??
          typedError.message ??
          "An unknown error occurred while updating the routing plan.";
        ToastNotification.error(errorMessage);
      }
    };
    const deleteRoutingPlanDetails = async () => {
      try {
        await deleteRoutingPlanDetailsApi(selectedRowIds);
        ToastNotification.success("Routing plan Details deleted successfully");
        onClearSelection();
        onRefresh();
        setDialogOpen(false);
      } catch {
        ToastNotification.error("Error deleting Routing plan Details.");
      }
    };

    const addBulkCustomer = async (data: Partial<DialogData>) => {
      try {
        const { customerId, routingPlanId } = data;

        if (!routingPlanId) {
          ToastNotification.error("Routing Plan ID is required.");
          return;
        }

        if (
          !customerId ||
          (Array.isArray(customerId) && customerId.length === 0)
        ) {
          ToastNotification.error("At least one Customer ID is required.");
          return;
        }

        console.log("Before sending data:", { routingPlanId, customerId });

        const customerIds = Array.isArray(customerId)
          ? customerId
          : [customerId];

        await CustomerBulkApi(routingPlanId, customerIds);

        onRefresh();
        closeDialog();
        ToastNotification.success("Routing plan details added successfully!");
      } catch (error) {
        console.error("Error adding routing plan details:", error);
        const typedError = error as ErrorResponse;
        const errorMessage =
          typedError.response?.data?.message ||
          typedError.message ||
          "An unknown error occurred while adding the routing plan.";
        ToastNotification.error(errorMessage, { timeOut: 3000 });
      }
    };

    const fieldHistory = useMemo<
      IField<RoutingPlanDetailsType & { historyTimestamp: Date }>[]
    >(
      () => [
        {
          key: "routingPlanId",
          header: "Routing Plan",
          transform: async (t) => {
            const id = String(t.routingPlanId);
            if (!id) return "-";
            try {
              const plan = await queryClient.fetchQuery({
                queryKey: ["routingPlan", id],
                queryFn: () => fetchRoutingPlan(id),
              });
              return plan?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "providerId",
          header: "Provider",
          transform: async (t) => {
            if (!t.providerId) return "-";
            try {
              const provider = await queryClient.fetchQuery({
                queryKey: ["provider", t.providerId],
                queryFn: () => fetchProvider(t.providerId),
              });
              const percentage = t.percentage0 ? ` (${t.percentage0}%)` : "";
              return `${provider?.name ?? "-"}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "provider1Id",
          header: "Provider 1",
          transform: async (t) => {
            if (!t.provider1Id) return "-";
            try {
              const provider = await queryClient.fetchQuery({
                queryKey: ["provider", t.provider1Id],
                queryFn: () => fetchProvider(t.provider1Id!),
              });
              const percentage = t.percentage1 ? ` (${t.percentage1}%)` : "";
              return `${provider?.name ?? "-"}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "provider2Id",
          header: "Provider 2",
          transform: async (t) => {
            if (!t.provider2Id) return "-";
            try {
              const provider = await queryClient.fetchQuery({
                queryKey: ["provider", t.provider2Id],
                queryFn: () => fetchProvider(t.provider2Id!),
              });
              const percentage = t.percentage2 ? ` (${t.percentage2}%)` : "";
              return `${provider?.name ?? "-"}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "provider3Id",
          header: "Provider 3",
          transform: async (t) => {
            if (!t.provider3Id) return "-";
            try {
              const provider = await queryClient.fetchQuery({
                queryKey: ["provider", t.provider3Id],
                queryFn: () => fetchProvider(t.provider3Id!),
              });
              const percentage = t.percentage3 ? ` (${t.percentage3}%)` : "";
              return `${provider?.name ?? "-"}${percentage}`;
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "Country",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const operator = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(Number(t.operatorId)),
              });
              if (!operator?.countryId) return "-";
              const country = await queryClient.fetchQuery({
                queryKey: ["country", operator.countryId],
                queryFn: () => fetchCountry(operator.countryId),
              });
              return country?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "Operator",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const operator = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(Number(t.operatorId)),
              });
              return operator?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "CC",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const operator = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(Number(t.operatorId)),
              });
              return operator?.cc ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "MCC",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const operator = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(Number(t.operatorId)),
              });
              return operator?.mcc?.join(",") ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "operatorId",
          header: "MNC",
          transform: async (t) => {
            if (!t.operatorId) return "-";
            try {
              const operator = await queryClient.fetchQuery({
                queryKey: ["operator", t.operatorId],
                queryFn: () => fetchOperator(Number(t.operatorId)),
              });
              return <TruncatedText text={operator?.mnc?.join(",")} />;
            } catch {
              return "-";
            }
          },
        },
        // {
        //   key: "cost",
        //   header: "Total Cost",
        //   transform: async (t) => {
        //     const routingCost = t.cost ?? 0;
        //     const hlrCost = t.hlrCost ?? 0;
        //     const mnpCost = t.mnpCost ?? 0;
        //     const totalCost = routingCost + hlrCost + mnpCost;
        //     const roundedTotalCost =
        //       Math.round((totalCost + Number.EPSILON) * 1e7) / 1e7;

        //     if (!hlrCost && !mnpCost) {
        //       return roundedTotalCost;
        //     }

        //     let breakdown = `${roundedTotalCost} (Routing: ${Math.round((routingCost + Number.EPSILON) * 1e7) / 1e7}`;
        //     if (hlrCost > 0)
        //       breakdown += `, HLR: ${Math.round((hlrCost + Number.EPSILON) * 1e7) / 1e7}`;
        //     if (mnpCost > 0)
        //       breakdown += `, MNP: ${Math.round((mnpCost + Number.EPSILON) * 1e7) / 1e7}`;
        //     breakdown += `)`;
        //     return breakdown;
        //   },
        // },
        {
          key: "rateType",
          header: "Rate Type",
          transform: async (t) => {
            const option = rateTypeOptions.find(
              (opt) => opt.value === t.rateType,
            );
            return option?.label ?? "-";
          },
        },
        { key: "rateModifier", header: "Rate Modifier" },
        { key: "rate", header: "Rate" },
        { key: "profit", header: "Profit" },
        { key: "lastRate", header: "Last Rate" },
        {
          key: "hlrServiceProviderId",
          header: "HLR Service Provider",
          transform: async (t) => {
            if (!t.hlrServiceProviderId) return "-";
            try {
              const sp = await queryClient.fetchQuery({
                queryKey: ["serviceProvider", Number(t.hlrServiceProviderId)],
                queryFn: () => getServiceProviderById(t.hlrServiceProviderId!),
              });
              return sp?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        {
          key: "mnpServiceProviderId",
          header: "MNP Service Provider",
          transform: async (t) => {
            if (!t.mnpServiceProviderId) return "-";
            try {
              const sp = await queryClient.fetchQuery({
                queryKey: ["serviceProvider", Number(t.mnpServiceProviderId)],
                queryFn: () => getServiceProviderById(t.mnpServiceProviderId!),
              });
              return sp?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        { key: "effectiveDate", header: "Effective Date" },
        {
          key: "providerId",
          header: "Currency",
          transform: async (t) => {
            if (!t.providerId) return "-";
            try {
              const provider = await queryClient.fetchQuery({
                queryKey: ["provider", t.providerId],
                queryFn: () => fetchProvider(t.providerId),
              });
              const profile = await queryClient.fetchQuery({
                queryKey: ["profile", provider.profileId],
                queryFn: () => fetchProfile(provider.profileId),
              });
              const currency = await queryClient.fetchQuery({
                queryKey: ["currency", profile.currencyId],
                queryFn: () => fetchCurrency(Number(profile.currencyId)),
              });
              return currency?.name ?? "-";
            } catch {
              return "-";
            }
          },
        },
        { key: "historyTimestamp", header: "Date Modified" },
        { key: "createdBy", header: "Created By" },
        { key: "updatedBy", header: "Updated By" },
      ],
      [queryClient],
    );

    const operatorLabelById = new Map<number, string>();
    [...operatorsByCountry, ...sortedOperatorsByCountry].forEach((option) => {
      operatorLabelById.set(Number(option.value), option.label);
    });

    const operatorRateRows = getSelectedOperatorIds(dialogData.operatorId).map(
      (opId) => {
        const cost = liveCostByOperator[opId]?.totalConvertedCost;
        return {
          operatorId: opId,
          label: operatorLabelById.get(opId) ?? `Operator ${opId}`,
          cost,
          rate: computeRate(dialogData.rateType, dialogData.rateModifier, cost),
        };
      },
    );

    return (
      <>
        <CustomDialog
          open={dialogOpen}
          onClose={closeDialog}
          mode={dialogMode}
          title={
            dialogMode === "history"
              ? "History"
              : dialogMode === "add"
                ? "Add Route"
                : dialogMode === "delete"
                  ? "Delete Route"
                  : dialogMode === "Bulk Customer"
                    ? "Bulk Customer"
                    : dialogMode === "Accounts"
                      ? "Linked Accounts Names"
                      : dialogMode === "impacted-instructions"
                        ? "Impacted Instructions"
                        : dialogMode === "Bulk Rate / Route"
                          ? "Bulk Rate / Route"
                          : dialogMode === "Bulk MNP / HLR"
                            ? "Bulk MNP / HLR"
                            : "Edit Route"
          }
          fields={
            dialogMode === "history"
              ? [
                  {
                    type: "custom",
                    component: (
                      <History
                        filters={appliedFilters}
                        fields={fieldHistory}
                        fetchFunc={({ size = 10 }) =>
                          fetchRoutingPlanDetailsHistory(String(selectedId), {
                            ...{
                              historyTimestamp: appliedFilters.historyTimestamp
                                ? String(appliedFilters.historyTimestamp)
                                : undefined,
                            },
                            size,
                          })
                        }
                      />
                    ),
                  },
                ]
              : dialogMode === "Bulk Rate / Route"
                ? [
                    {
                      type: "custom",
                      component: (
                        <BulkRateRouteContent
                          request={bulkRateRouteRequest}
                          setRequest={setBulkRateRouteRequest}
                        />
                      ),
                    },
                  ]
                : dialogMode === "Bulk MNP / HLR"
                  ? [
                      {
                        type: "custom",
                        component: (
                          <BulkMnpHlrContent
                            request={bulkMnpHlrRequest}
                            setRequest={setBulkMnpHlrRequest}
                            mnpServiceProviderOptions={
                              mnpServiceProviderOptions
                            }
                            hlrServiceProviderOptions={
                              hlrServiceProviderOptions
                            }
                          />
                        ),
                      },
                    ]
                  : dialogMode === "impacted-instructions"
                    ? [
                        {
                          type: "custom",
                          component: !selectedId ? (
                            <div>Unexpected error: ID is missing</div>
                          ) : (
                            <ImpactedInstructions
                              id={selectedId}
                              open={dialogOpen}
                            />
                          ),
                        },
                      ]
                    : dialogMode === "delete"
                      ? [
                          {
                            type: "custom",
                            component: (
                              <Box>
                                <p>
                                  Are you sure you want to delete this route?
                                </p>
                              </Box>
                            ),
                          },
                        ]
                      : dialogMode === "Accounts"
                        ? [
                            {
                              type: "custom",
                              component: (
                                <AccountNamesTable
                                  accountNames={accountNames || []}
                                />
                              ),
                            },
                          ]
                        : dialogMode === "Bulk Customer"
                          ? [
                              {
                                type: "custom",
                                component: (
                                  <AutoComplete
                                    label="Routing Plan"
                                    value={dialogData.routingPlanId ?? ""}
                                    options={RoutingPlanDetailsOptions}
                                    onChange={(
                                      value:
                                        | string
                                        | number
                                        | (string | number)[],
                                    ) => {
                                      setDialogData((prev) => ({
                                        ...prev,
                                        routingPlanId: String(value),
                                      }));
                                      validateField(
                                        "routingPlan",
                                        String(value),
                                      );
                                    }}
                                    clearable={true}
                                    disablePortal={false}
                                    error={!!errors.routingPlan}
                                    placeholder="Select Routing Plan"
                                    highlightLabelStar={true}
                                    helperText={
                                      errors.routingPlan
                                        ? "Routing Plan is required"
                                        : ""
                                    }
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        backgroundColor: COLORS.WhiteBackground,
                                      },
                                    }}
                                  />
                                ),
                              },
                              {
                                type: "custom",
                                component: (
                                  <AutoComplete
                                    label="Customer"
                                    multiple={true}
                                    selectAll={true}
                                    hideChips={true}
                                    value={
                                      Array.isArray(dialogData.customerId)
                                        ? dialogData.customerId.map(String)
                                        : []
                                    }
                                    options={customersOptions}
                                    onChange={(
                                      value:
                                        | string
                                        | number
                                        | (string | number)[],
                                    ) => {
                                      const valueArray: number[] =
                                        Array.isArray(value)
                                          ? value.map((v) => Number(v))
                                          : value
                                            ? [Number(value)]
                                            : [];

                                      console.log(
                                        "Updated operatorId:",
                                        valueArray,
                                      );

                                      setDialogData((prev) => ({
                                        ...prev,
                                        customerId: valueArray,
                                      }));
                                    }}
                                    clearable={true}
                                    disablePortal={false}
                                    error={!!errors.customerId}
                                    placeholder="Select Customer"
                                    highlightLabelStar={true}
                                    helperText={
                                      errors.customerId
                                        ? "Customer is required"
                                        : ""
                                    }
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        backgroundColor: COLORS.WhiteBackground,
                                      },
                                    }}
                                  />
                                ),
                              },
                            ]
                          : [
                              {
                                type: "custom",
                                component: (
                                  <AutoComplete
                                    label="Routing Plan"
                                    value={dialogData.routingPlanId ?? ""}
                                    options={RoutingPlanDetailsOptions}
                                    onChange={(
                                      value:
                                        | string
                                        | number
                                        | (string | number)[],
                                    ) => {
                                      setDialogData((prev) => ({
                                        ...prev,
                                        routingPlanId: String(value),
                                      }));
                                      validateField(
                                        "routingPlan",
                                        String(value),
                                      );
                                    }}
                                    clearable={true}
                                    disablePortal={false}
                                    error={!!errors.routingPlan}
                                    placeholder="Select Routing Plan"
                                    highlightLabelStar={true}
                                    helperText={
                                      errors.routingPlan
                                        ? "Routing Plan is required"
                                        : ""
                                    }
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        backgroundColor: COLORS.WhiteBackground,
                                      },
                                    }}
                                  />
                                ),
                              },
                              {
                                type: "custom",
                                component: (
                                  <AutoComplete
                                    label="Provider"
                                    value={dialogData.providerId ?? ""}
                                    options={providersOptions}
                                    onChange={handleProviderChange}
                                    clearable={true}
                                    disablePortal={false}
                                    error={!!errors.providerId}
                                    placeholder="Select Provider"
                                    highlightLabelStar={true}
                                    helperText={
                                      errors.providerId
                                        ? "Provider is required"
                                        : ""
                                    }
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        backgroundColor: COLORS.WhiteBackground,
                                      },
                                    }}
                                  />
                                ),
                              },
                              {
                                type: "custom",
                                component: (
                                  <AutoComplete
                                    label="Country"
                                    value={dialogData.countryId ?? ""}
                                    options={countriesOptions}
                                    onChange={handleCountryChange}
                                    clearable={true}
                                    disablePortal={false}
                                    error={!!errors.country}
                                    placeholder="Select Country"
                                    highlightLabelStar={true}
                                    helperText={
                                      errors.country
                                        ? "Country is required"
                                        : ""
                                    }
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        backgroundColor: COLORS.WhiteBackground,
                                      },
                                    }}
                                  />
                                ),
                              },
                              {
                                type: "custom",
                                component: (
                                  <AutoComplete
                                    label="Operator"
                                    multiple={true}
                                    selectAll={true}
                                    hideChips={true}
                                    value={
                                      Array.isArray(dialogData.operatorId)
                                        ? dialogData.operatorId.map(String)
                                        : []
                                    }
                                    options={sortedOperatorsByCountry}
                                    onChange={(
                                      value:
                                        | string
                                        | number
                                        | (string | number)[],
                                    ) => {
                                      const valueArray: number[] =
                                        Array.isArray(value)
                                          ? value.map((v) => Number(v))
                                          : value
                                            ? [Number(value)]
                                            : [];

                                      console.log(
                                        "Updated operatorId:",
                                        valueArray,
                                      );

                                      setDialogData((prev) => ({
                                        ...prev,
                                        operatorId: valueArray,
                                      }));
                                    }}
                                    onOpen={handleOperatorDropdownOpen}
                                    getOptionDisabled={
                                      hasFetchedCostPlan
                                        ? (option) =>
                                            !operatorsWithCostIds.has(
                                              Number(option.value),
                                            )
                                        : undefined
                                    }
                                    clearable={true}
                                    disablePortal={false}
                                    error={!!errors.operatorId}
                                    placeholder="Select Operator"
                                    highlightLabelStar={true}
                                    helperText={
                                      errors.operatorId
                                        ? "Operator is required"
                                        : ""
                                    }
                                    disabled={isOperatorEffectivelyDisabled}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        backgroundColor: COLORS.WhiteBackground,
                                      },
                                    }}
                                  />
                                ),
                              },
                              {
                                type: "select",
                                label: "Rate Type",
                                bgColor: COLORS.WhiteBackground,
                                highlightLabelStar: true,
                                value: dialogData.rateType ?? "",
                                options: rateTypeOptions,
                                onChange: handleRateTypeChange,
                                placeholder: "Select Rate Type",
                                error: !!errors.rateType,
                                helperText: errors.rateType
                                  ? "Rate Type is required"
                                  : "",
                              },
                              {
                                type: "text",
                                typeinput: "number",
                                label: "Rate Modifier",
                                bgColor: COLORS.WhiteBackground,
                                highlightLabelStar: true,
                                value:
                                  dialogData.rateModifier !== undefined
                                    ? Number(dialogData.rateModifier)
                                    : "",
                                onChange: handleRateModifierChange,
                                placeholder: "Enter Rate Modifier",
                                error: !!errors.rateModifier,
                                helperText: errors.rateModifier
                                  ? "Rate Modifier is required"
                                  : "",
                              },
                              {
                                type: "custom",
                                label: "Formula Rate (Calculated)",
                                component: (
                                  <Box
                                    sx={{
                                      width: "100%",
                                      borderRadius: "10px",
                                      border: `1px solid ${
                                        errors.rate
                                          ? COLORS.RED
                                          : COLORS.goldChipBorder
                                      }`,
                                      background: `linear-gradient(135deg, ${COLORS.goldChipBackground} 0%, ${COLORS.WhiteBackground} 100%)`,
                                      px: 2,
                                      py: 1.25,
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.25,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontSize: "0.8rem",
                                          fontWeight: 600,
                                          color: COLORS.typographyColor,
                                        }}
                                      >
                                        Formula Rate (Calculated)
                                      </Typography>
                                      <Tooltip
                                        title="Automatically calculated from Rate Type, Rate Modifier, and the live Cost (fetched as you change provider/rate inputs)."
                                        arrow
                                      >
                                        <HelpOutlineIcon
                                          sx={{
                                            fontSize: 15,
                                            color: COLORS.placeHolderColor,
                                            cursor: "help",
                                          }}
                                        />
                                      </Tooltip>
                                    </Box>
                                    {isLiveCostLoading ? (
                                      <Typography
                                        sx={{
                                          fontSize: "1.4rem",
                                          fontWeight: 700,
                                          color: COLORS.GOLD,
                                          lineHeight: 1.3,
                                        }}
                                      >
                                        Calculating...
                                      </Typography>
                                    ) : operatorRateRows.length === 0 ? (
                                      <>
                                        <Typography
                                          sx={{
                                            fontSize: "1.4rem",
                                            fontWeight: 700,
                                            color: COLORS.GOLD,
                                            lineHeight: 1.3,
                                          }}
                                        >
                                          —
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: "0.72rem",
                                            color: errors.rate
                                              ? COLORS.RED
                                              : COLORS.defaultGray,
                                          }}
                                        >
                                          {errors.rate
                                            ? "Rate is required"
                                            : "Select an Operator, Rate Type & Rate Modifier to calculate"}
                                        </Typography>
                                      </>
                                    ) : operatorRateRows.length === 1 ? (
                                      <>
                                        <Typography
                                          sx={{
                                            fontSize: "1.4rem",
                                            fontWeight: 700,
                                            color: COLORS.GOLD,
                                            lineHeight: 1.3,
                                          }}
                                        >
                                          {operatorRateRows[0].rate !== undefined
                                            ? Number(operatorRateRows[0].rate)
                                            : "—"}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: "0.72rem",
                                            color: errors.rate
                                              ? COLORS.RED
                                              : COLORS.defaultGray,
                                          }}
                                        >
                                          {errors.rate
                                            ? "Rate is required"
                                            : operatorRateRows[0].cost !==
                                                undefined
                                              ? `Based on cost: ${operatorRateRows[0].cost}`
                                              : "Set Rate Type & Rate Modifier to calculate"}
                                        </Typography>
                                      </>
                                    ) : (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          flexDirection: "column",
                                          gap: 0.5,
                                          mt: 0.25,
                                        }}
                                      >
                                        {operatorRateRows.map((row) => (
                                          <Box
                                            key={row.operatorId}
                                            sx={{
                                              display: "flex",
                                              justifyContent: "space-between",
                                              alignItems: "baseline",
                                              gap: 1,
                                              borderBottom: `1px dashed ${COLORS.goldChipBorder}`,
                                              pb: 0.5,
                                              "&:last-of-type": {
                                                borderBottom: "none",
                                                pb: 0,
                                              },
                                            }}
                                          >
                                            <Typography
                                              sx={{
                                                fontSize: "0.78rem",
                                                color: COLORS.typographyColor,
                                                fontWeight: 500,
                                              }}
                                            >
                                              {row.label}
                                              {row.cost !== undefined && (
                                                <Typography
                                                  component="span"
                                                  sx={{
                                                    fontSize: "0.68rem",
                                                    color: COLORS.defaultGray,
                                                    ml: 0.5,
                                                  }}
                                                >
                                                  (cost: {row.cost})
                                                </Typography>
                                              )}
                                            </Typography>
                                            <Typography
                                              sx={{
                                                fontSize: "1rem",
                                                fontWeight: 700,
                                                color: COLORS.GOLD,
                                              }}
                                            >
                                              {row.rate !== undefined
                                                ? Number(row.rate)
                                                : "—"}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                ),
                              },
                              {
                                type: "custom",
                                component: (
                                  <AutoComplete
                                    label="HLR Service Provider"
                                    value={
                                      dialogData.hlrServiceProviderId ?? ""
                                    }
                                    options={hlrServiceProviderOptions}
                                    onChange={(
                                      value:
                                        | string
                                        | number
                                        | (string | number)[],
                                    ) => {
                                      setDialogData((prev) => ({
                                        ...prev,
                                        hlrServiceProviderId: value
                                          ? String(value)
                                          : undefined,
                                      }));
                                    }}
                                    clearable={true}
                                    disablePortal={false}
                                    placeholder="Select HLR Service Provider"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        backgroundColor: COLORS.WhiteBackground,
                                      },
                                    }}
                                  />
                                ),
                              },
                              {
                                type: "custom",
                                component: (
                                  <AutoComplete
                                    label="MNP Service Provider"
                                    value={
                                      dialogData.mnpServiceProviderId ?? ""
                                    }
                                    options={mnpServiceProviderOptions}
                                    onChange={(
                                      value:
                                        | string
                                        | number
                                        | (string | number)[],
                                    ) => {
                                      setDialogData((prev) => ({
                                        ...prev,
                                        mnpServiceProviderId: value
                                          ? String(value)
                                          : undefined,
                                      }));
                                    }}
                                    clearable={true}
                                    disablePortal={false}
                                    placeholder="Select MNP Service Provider"
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        backgroundColor: COLORS.WhiteBackground,
                                      },
                                    }}
                                  />
                                ),
                              },

                              {
                                type: "combined",
                                label: "Blending",
                                showLabel: true,
                                fields: [
                                  {
                                    type: "custom",
                                    component: (
                                      <AutoComplete
                                        value={dialogData.provider1Id ?? ""}
                                        options={providersOptions}
                                        onChange={(
                                          value:
                                            | string
                                            | number
                                            | (string | number)[],
                                        ) => {
                                          setDialogData((prev) => ({
                                            ...prev,
                                            provider1Id: Array.isArray(value)
                                              ? value.join(",")
                                              : String(value),
                                          }));
                                        }}
                                        clearable={true}
                                        disablePortal={false}
                                        error={!!errors.provider1Id}
                                        placeholder="Provider 1"
                                        helperText={
                                          errors.provider1Id
                                            ? "Provider is required"
                                            : ""
                                        }
                                        width="auto"
                                        sx={{
                                          flex: 1,
                                          "& .MuiOutlinedInput-root": {
                                            backgroundColor:
                                              COLORS.WhiteBackground,
                                          },
                                        }}
                                      />
                                    ),
                                  },
                                  {
                                    type: "text",
                                    value: dialogData.percentage1 || "",
                                    placeholder: "Percentage 1",
                                    bgColor: COLORS.WhiteBackground,
                                    onChange: (value: string | number) => {
                                      const numericValue = Number(value);
                                      const cappedValue =
                                        numericValue > 100 ? 100 : numericValue;
                                      setDialogData((prev) => ({
                                        ...prev,
                                        percentage1: cappedValue,
                                      }));
                                    },
                                    error: !!errors.percentage1,
                                    helperText: errors.percentage1
                                      ? "Percentage 1 is required and must be 100 or less"
                                      : "",
                                    sx: { flex: 1 },
                                  },
                                  {
                                    type: "custom",
                                    component: (
                                      <AutoComplete
                                        value={dialogData.provider2Id ?? ""}
                                        options={providersOptions}
                                        onChange={(
                                          value:
                                            | string
                                            | number
                                            | (string | number)[],
                                        ) => {
                                          setDialogData((prev) => ({
                                            ...prev,
                                            provider2Id: Array.isArray(value)
                                              ? value.join(",")
                                              : String(value),
                                          }));
                                        }}
                                        clearable={true}
                                        disablePortal={false}
                                        error={!!errors.provider2Id}
                                        placeholder="Provider 2"
                                        helperText={
                                          errors.provider2Id
                                            ? "Provider is required"
                                            : ""
                                        }
                                        width="auto"
                                        sx={{
                                          flex: 1,
                                          "& .MuiOutlinedInput-root": {
                                            backgroundColor:
                                              COLORS.WhiteBackground,
                                          },
                                        }}
                                      />
                                    ),
                                  },
                                  {
                                    type: "text",
                                    value: dialogData.percentage2 || "",
                                    placeholder: "Percentage 2",
                                    bgColor: COLORS.WhiteBackground,
                                    onChange: (value: string | number) => {
                                      const numericValue = Number(value);
                                      const cappedValue =
                                        numericValue > 100 ? 100 : numericValue;
                                      setDialogData((prev) => ({
                                        ...prev,
                                        percentage2: cappedValue,
                                      }));
                                    },
                                    error: !!errors.percentage2,
                                    helperText: errors.percentage2
                                      ? "Percentage 2 is required"
                                      : "",
                                    sx: { flex: 1 },
                                  },
                                  {
                                    type: "custom",
                                    component: (
                                      <AutoComplete
                                        value={dialogData.provider3Id ?? ""}
                                        options={providersOptions}
                                        onChange={(
                                          value:
                                            | string
                                            | number
                                            | (string | number)[],
                                        ) => {
                                          setDialogData((prev) => ({
                                            ...prev,
                                            provider3Id: Array.isArray(value)
                                              ? value.join(",")
                                              : String(value),
                                          }));
                                        }}
                                        clearable={true}
                                        disablePortal={false}
                                        error={!!errors.provider3Id}
                                        placeholder="Provider 3"
                                        helperText={
                                          errors.provider3Id
                                            ? "Provider is required"
                                            : ""
                                        }
                                        width="auto"
                                        sx={{
                                          flex: 1,
                                          "& .MuiOutlinedInput-root": {
                                            backgroundColor:
                                              COLORS.WhiteBackground,
                                          },
                                        }}
                                      />
                                    ),
                                  },
                                  {
                                    type: "text",
                                    value: dialogData.percentage3 || "",
                                    placeholder: "Percentage 3",
                                    bgColor: COLORS.WhiteBackground,
                                    onChange: (value: string | number) => {
                                      const numericValue = Number(value);
                                      const cappedValue =
                                        numericValue > 100 ? 100 : numericValue;
                                      setDialogData((prev) => ({
                                        ...prev,
                                        percentage3: cappedValue,
                                      }));
                                    },
                                    error: !!errors.percentage3,
                                    helperText: errors.percentage3
                                      ? "Percentage 3 is required"
                                      : "",
                                    sx: { flex: 1 },
                                  },
                                ],
                                layout: "column",
                                sx: { gap: 2 },
                              },
                            ]
          }
          buttons={
            dialogMode === "history"
              ? []
              : dialogMode === "Accounts"
                ? []
                : dialogMode === "impacted-instructions"
                  ? []
                  : [
                      dialogMode !== "delete"
                        ? {
                            label: "Save",
                            variant: "contained",
                            bgColor: COLORS.GOLD,
                            borderRadius: "12px",
                            onClick: async () => {
                              if (dialogMode === "edit") {
                                const baseName = "Routing_plan";
                                const generatedName =
                                  generateUniqueName(baseName);
                                const updatedData = {
                                  ...dialogData,
                                  name: generatedName,
                                  operatorId: dialogData.operatorId,
                                };
                                await editRoutingPlanDetails(updatedData);
                              } else if (dialogMode === "Bulk Customer") {
                                const bulkData = {
                                  routingPlanId: dialogData.routingPlanId,
                                  customerId: dialogData.customerId,
                                };
                                await addBulkCustomer(bulkData);
                              } else if (dialogMode === "Bulk Rate / Route") {
                                const bulkRateRouteResult: BulkRateRouteResponse =
                                  await bulkRateRouteApi(
                                    bulkRateRouteRequest as BulkRateRouteRequest,
                                    selectedRowIds,
                                  );
                                setBulkRateRouteRequest({
                                  type: BulkRateRouteRequestType.RATE,
                                  rateType: RateType.VALUE,
                                  rateModifier: 0,
                                  providerId: "",
                                });
                                setDialogOpen(false);
                                onRefresh();
                                if (
                                  bulkRateRouteResult.failed > 0 &&
                                  bulkRateRouteResult.applied > 0
                                ) {
                                  ToastNotification.warning(
                                    `Applied: ${bulkRateRouteResult.applied}, Failed: ${bulkRateRouteResult.failed}. ${bulkRateRouteResult.failureReason ?? ""}`.trim(),
                                  );
                                } else if (
                                  bulkRateRouteResult.failed > 0 &&
                                  bulkRateRouteResult.applied === 0
                                ) {
                                  ToastNotification.error(
                                    `Applied: ${bulkRateRouteResult.applied}, Failed: ${bulkRateRouteResult.failed}. ${bulkRateRouteResult.failureReason ?? ""}`.trim(),
                                  );
                                } else {
                                  ToastNotification.success(
                                    `Bulk rate/route updated successfully! Applied: ${bulkRateRouteResult.applied}.`,
                                  );
                                }
                              } else if (dialogMode === "Bulk MNP / HLR") {
                                try {
                                  await bulkMnpHlrApi(
                                    bulkMnpHlrRequest as BulkMnpHlrRequest,
                                    selectedRowIds,
                                  );
                                  setBulkMnpHlrRequest({
                                    type: BulkMnpHlrRequestType.MNP,
                                  });
                                  setDialogOpen(false);
                                  onRefresh();
                                  ToastNotification.success(
                                    "Bulk MNP/HLR updated successfully!",
                                  );
                                } catch (error) {
                                  const typedError = error as ErrorResponse;
                                  const errorMessage =
                                    typedError.response?.data?.message ||
                                    typedError.message ||
                                    "An error occurred while performing bulk MNP/HLR update.";
                                  ToastNotification.error(errorMessage, {
                                    timeOut: 3000,
                                  });
                                }
                              } else {
                                const baseName = "Routing_plan";
                                const generatedName =
                                  generateUniqueName(baseName);
                                const filteredOperatorId =
                                  getFilteredOperatorId();
                                const updatedData = {
                                  ...dialogData,
                                  name: generatedName,
                                  operatorId: filteredOperatorId,
                                };
                                await addRoutingPlanDetails(updatedData);
                              }
                            },
                            disabled:
                              isSaveDisabled ||
                              (dialogMode === "edit" &&
                                !hasJwtPermission("PUT_RATED_ROUTING")) ||
                              (dialogMode === "Bulk Customer" &&
                                !hasJwtPermission("BULK_CUSTOMERS")) ||
                              (dialogMode === "Bulk Rate / Route" &&
                                !hasJwtPermission("BULK_RATE_ROUTING")) ||
                              (dialogMode === "Bulk MNP / HLR" &&
                                !hasJwtPermission("BULK_MNP_HLR")) ||
                              (dialogMode !== "edit" &&
                                dialogMode !== "Bulk Customer" &&
                                dialogMode !== "Bulk Rate / Route" &&
                                dialogMode !== "Bulk MNP / HLR" &&
                                !hasJwtPermission("POST_RATED_ROUTING")),
                          }
                        : {
                            label: "Delete",
                            variant: "contained",
                            bgColor: COLORS.GOLD,
                            textColor: COLORS.iconColor,
                            borderRadius: "12px",
                            onClick: deleteRoutingPlanDetails,
                            disabled: !hasJwtPermission("DELETE_RATED_ROUTING"),
                          },
                    ]
          }
        />
        {dialogMode === "Send Rate" && (
          <CustomDialog
            open={sendDialogOpen}
            onClose={closeDialog}
            mode={dialogMode}
            title="Send Rate sheet"
            fields={[
              {
                type: "custom",
                component: (
                  <Box sx={{ width: "100%" }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <CustomLabel
                        text="Routing Plan"
                        fontSize="14px"
                        fontWeight={500}
                        color={COLORS.typographyColor}
                        highlightLabelStar={true}
                      />
                    </Box>
                    <AutoComplete
                      multiple={true}
                      // selectAll={true}
                      selectFew={5}
                      hideChips={true}
                      value={
                        Array.isArray(dialogData.routingPlanId)
                          ? dialogData.routingPlanId
                          : []
                      }
                      onChange={(
                        value: string | number | (string | number)[],
                      ) => {
                        setDialogData((prev) => {
                          let newValue: string | string[];
                          if (Array.isArray(value)) {
                            newValue = value.map(String);
                          } else {
                            newValue = String(value);
                          }
                          return {
                            ...prev,
                            routingPlanId: newValue,
                          };
                        });
                      }}
                      options={RoutingPlanWithRateChangeOptions}
                      error={!!errors.routingPlan}
                      helperText={
                        errors.routingPlan ? "Routing Plan is required" : ""
                      }
                      placeholder="Select Routing Plans"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: COLORS.WhiteBackground,
                        },
                      }}
                      labelComponent={
                        <Tooltip
                          title="Note: These Routing Plans should have a rate
                              change and a client assigned. Only routing plans for accounts under this user will be displayed."
                        >
                          <IconButton
                            size="small"
                            onClick={() => setOpenNoteDialog(true)}
                            sx={{ p: 0.5 }}
                          >
                            <HelpOutlineIcon fontSize="medium" />
                          </IconButton>
                        </Tooltip>
                      }
                    />
                  </Box>
                ),
              },
              {
                type: "custom",
                component: (
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-start",
                      gap: 3,
                    }}
                  >
                    <CustomCheckbox
                      checked={!(dialogData.sendonlyChange ?? false)}
                      onChange={(e) =>
                        handleSendonlyChangeToggle(!e.target.checked)
                      }
                      label="Include A-Z"
                    />
                    <CustomCheckbox
                      checked={dialogData.sendToCustomer ?? true}
                      onChange={(e) =>
                        setDialogData((prev) => ({
                          ...prev,
                          sendToCustomer: e.target.checked,
                          testEmail: e.target.checked
                            ? undefined
                            : DEFAULT_TEST_EMAIL,
                        }))
                      }
                      label="Send to customer"
                    />
                  </Box>
                ),
              },
              ...(!(dialogData.sendToCustomer ?? true)
                ? [
                    {
                      type: "custom" as const,
                      component: (
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1.5,
                            borderRadius: "12px",
                            border: `1px solid ${COLORS.GOLD}`,
                            backgroundColor: COLORS.WhiteBackground,
                          }}
                        >
                          <MailOutlineIcon sx={{ color: COLORS.GOLD }} />
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{ color: COLORS.placeHolderColor }}
                            >
                              Rate sheet will be sent to
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {DEFAULT_TEST_EMAIL}
                            </Typography>
                          </Box>
                        </Box>
                      ),
                    },
                  ]
                : []),
            ]}
            buttons={[
              {
                label: previewLoading ? "Loading..." : "Preview",
                variant: "contained",
                bgColor: COLORS.GOLD,
                borderRadius: "12px",
                onClick: handlePreviewClick,
                disabled:
                  isSaveDisabled ||
                  !hasJwtPermission("PREVIEW_RATE_NOTIFICATION") ||
                  previewLoading ||
                  !dialogData.routingPlanId ||
                  (Array.isArray(dialogData.routingPlanId) &&
                    dialogData.routingPlanId.length === 0),
              },
            ]}
          />
        )}
        <CustomDialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          mode="history"
          title="Rate Notification Preview"
          contentSx={{ paddingBottom: 0 }}
          actionsSx={{ paddingTop: 0 }}
          titleAction={
            <CustomButton
              size="small"
              variant="outlined"
              label={emailTemplatesLoading ? "Loading..." : "View Templates"}
              onClick={handleViewEmailTemplates}
              textColor={COLORS.GOLD}
              borderColor={COLORS.GOLD}
              disabled={
                emailTemplatesLoading ||
                !hasJwtPermission("GET_RATE_CHANGE_TEMPLATE")
              }
            />
          }
          fields={[
            {
              type: "custom",
              component: (
                <Box sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      mb: 2,
                      display: "flex",
                      justifyContent: "flex-start",
                      gap: 3,
                    }}
                  >
                    <CustomCheckbox
                      checked={!(dialogData.sendonlyChange ?? false)}
                      onChange={(e) =>
                        handleSendonlyChangeToggle(!e.target.checked)
                      }
                      label="Include A-Z"
                    />
                    <CustomCheckbox
                      checked={dialogData.attachCcEmail ?? true}
                      onChange={(e) =>
                        setDialogData((prev) => ({
                          ...prev,
                          attachCcEmail: e.target.checked,
                        }))
                      }
                      label={
                        dialogData.sendToCustomer ?? true
                          ? "CC: Profiles' Account Manager(s) + Internal Account Manager"
                          : "CC: Internal Account Manager"
                      }
                    />
                  </Box>
                  {previewLoading ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight="200px"
                    >
                      <CircularProgress sx={{ color: COLORS.GOLD }} />
                    </Box>
                  ) : previewData.length === 0 ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      minHeight="200px"
                    >
                      <Typography color="textSecondary">
                        No rate changes found for the selected routing plans.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Tabs
                        value={previewTabIndex}
                        onChange={(_, newValue) => setPreviewTabIndex(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                          borderBottom: `1px solid ${COLORS.dividerColor}`,
                          "& .MuiTab-root": {
                            textTransform: "none",
                            fontWeight: 500,
                          },
                          "& .Mui-selected": {
                            color: `${COLORS.GOLD} !important`,
                          },
                          "& .MuiTabs-indicator": {
                            backgroundColor: COLORS.GOLD,
                          },
                        }}
                      >
                        {previewData.map((plan, index) => (
                          <Tab
                            key={plan.routingPlanId}
                            label={
                              <Box display="flex" alignItems="center" gap={1}>
                                <span>{plan.routingPlanName}</span>
                                <Box
                                  component="span"
                                  sx={{
                                    backgroundColor: COLORS.GOLD,
                                    color: COLORS.textPrimary,
                                    borderRadius: "12px",
                                    px: 1,
                                    py: 0.25,
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                  }}
                                >
                                  {plan.records.length}
                                </Box>
                              </Box>
                            }
                            value={index}
                          />
                        ))}
                      </Tabs>
                      {previewData.map((plan, index) => (
                        <Box
                          key={plan.routingPlanId}
                          role="tabpanel"
                          hidden={previewTabIndex !== index}
                          sx={{ p: 1, pt: 2 }}
                        >
                          {previewTabIndex === index && (
                            <>
                              {plan.records.length === 0 ? (
                                <Typography
                                  color="textSecondary"
                                  textAlign="center"
                                  py={4}
                                >
                                  No rate changes for this routing plan.
                                </Typography>
                              ) : (
                                <TableContainer
                                  sx={{
                                    maxHeight: "50vh",
                                    border: `1px solid ${COLORS.dividerColor}`,
                                    borderRadius: "8px",
                                  }}
                                >
                                  <Table stickyHeader size="small">
                                    <TableHead>
                                      <TableRow>
                                        {previewFields.map((field) => (
                                          <TableCell
                                            key={String(field.key)}
                                            sx={{
                                              fontWeight: 600,
                                              backgroundColor:
                                                COLORS.WhiteBackground,
                                              textAlign: "center",
                                            }}
                                          >
                                            {field.header}
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <DataTableBody<RateCsvRecord>
                                      fields={previewFields}
                                      content={plan.records}
                                    />
                                  </Table>
                                </TableContainer>
                              )}
                            </>
                          )}
                        </Box>
                      ))}
                    </>
                  )}
                </Box>
              ),
            },
          ]}
          buttons={[
            {
              label: "Send",
              variant: "contained",
              bgColor: COLORS.GOLD,
              borderRadius: "12px",
              onClick: async () => {
                try {
                  const routingPlanIds = Array.isArray(dialogData.routingPlanId)
                    ? dialogData.routingPlanId
                    : [String(dialogData.routingPlanId)];

                  const testEmail = dialogData.testEmail?.trim() || undefined;
                  const sendonlyChange = dialogData.sendonlyChange ?? false;
                  const updateLastRate = true;
                  const attachCcEmail = dialogData.attachCcEmail ?? true;

                  await sendRateNotificationApi(
                    routingPlanIds,
                    testEmail,
                    sendonlyChange,
                    updateLastRate,
                    attachCcEmail,
                  );

                  const successMessage = testEmail
                    ? `Rate Sheets sent successfully to ${testEmail}!`
                    : "Rate Sheets sent successfully!";
                  ToastNotification.success(successMessage);
                  onRefresh();
                  setPreviewDialogOpen(false);
                  setSendDialogOpen(false);
                } catch (error) {
                  const typedError = error as ErrorResponse;
                  const errorMessage =
                    typedError.response?.data?.message ||
                    typedError.message ||
                    "An unknown error occurred while sending emails.";
                  ToastNotification.error(errorMessage, { timeOut: 3000 });
                }
              },
              disabled:
                previewData.length === 0 ||
                !hasJwtPermission("SEND_RATE_NOTIFICATION"),
            },
          ]}
        />
        <CustomDialog
          open={emailTemplatesDialogOpen}
          onClose={() => setEmailTemplatesDialogOpen(false)}
          mode="Bulk Rate / Route"
          title="Email Templates"
          fields={[
            {
              type: "custom",
              component: emailTemplatesLoading ? (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="200px"
                >
                  <CircularProgress sx={{ color: COLORS.GOLD }} />
                </Box>
              ) : (
                <Box display="flex" flexDirection="column" gap={3}>
                  {/* rate change template */}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        mb: 1,
                        color: COLORS.successGreen,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      Rate Change Template
                    </Typography>
                    {emailTemplates.rateChange ? (
                      <Box
                        sx={{
                          border: `1px solid ${COLORS.dividerColor}`,
                          borderRadius: "8px",
                          p: 2,
                          backgroundColor: COLORS.lightGrayBackground,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          mb={1}
                        >
                          <strong>Subject:</strong>{" "}
                          {emailTemplates.rateChange.templateSubject}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          mb={1}
                        >
                          <strong>From:</strong>{" "}
                          {emailTemplates.rateChange.fromEmail || "-"}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          component="div"
                        >
                          <strong>Body:</strong>
                          <SafeHtml
                            html={emailTemplates.rateChange.templateBody}
                            sx={{
                              mt: 1,
                              p: 1,
                              backgroundColor: "#fff",
                              border: `1px solid ${COLORS.dividerColor}`,
                              borderRadius: "4px",
                              maxHeight: "150px",
                              overflow: "auto",
                              whiteSpace: "pre-wrap",
                              fontSize: "0.85rem",
                            }}
                          />
                        </Typography>
                      </Box>
                    ) : (
                      <Typography color="textSecondary" fontStyle="italic">
                        No RATE_CHANGE template found.
                      </Typography>
                    )}
                  </Box>
                </Box>
              ),
            },
          ]}
          buttons={[]}
        />
      </>
    );
  },
);

export default RoutingPlanDetailsPage;
